import {
  AddRounded,
  AttachFileOutlined,
  BusinessOutlined,
  CheckCircleOutlineRounded,
  ChevronRightOutlined,
  ContactsOutlined,
  DescriptionOutlined,
  EastRounded,
  EditOutlined,
  ExpandMoreRounded,
  FolderOpenOutlined,
  Functions,
  GroupsOutlined,
  HighlightOffRounded,
  HistoryOutlined,
  HomeOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  InsertDriveFileOutlined,
  KeyboardArrowDownRounded,
  KeyboardDoubleArrowRightRounded,
  OpenInNewRounded,
  PaidOutlined,
  PauseCircleOutlineRounded,
  PlayCircleOutlineRounded,
  RadioButtonUncheckedRounded,
  PercentOutlined,
  PersonOutline,
  QuestionAnswerOutlined,
  SearchOutlined,
  SettingsOutlined,
  TimelineOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import { isEmpty, isNil, size } from "lodash";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  dateOnlyMs,
  daysUntilDateOnly,
  formatDate,
} from "../../../../_src_shared/utils/functions/formatDate";
import {
  formatDateTime,
  timestampMs,
} from "../../../../_src_shared/utils/functions/formatDateTime";
import ActivityFeedItem from "../../sidebar/types/LoanChangelog/LoanActivityFeedItem";
import SelectInput from "../../../../components/inputs/SelectInput";
import SelectUserEmailInput from "../../../../components/inputs/SelectUserEmailInput";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  loanSubStatusOptionsMap,
  selectOptionsLoanStatus,
} from "../../../../constants/selectOptions/selectOptionsLoanStatus";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { useModelFieldOptions } from "../../../../hooks/useModelFieldOptions";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import {
  ACQUISITION_ON_HOLD_STATUS,
  ACQUISITION_STATUSES,
  acquisitionHeldStatus,
} from "../../../../pipelines/pipelineConfig";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { loanPipelineSet } from "../../../../redux/actions/loanPipelineActions";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

const MarkLoanClosedModal = lazy(
  () => import("../../modals/MarkLoanClosedModal"),
);
const MarkLoanDeadModal = lazy(() => import("../../modals/MarkLoanDeadModal"));

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
   Editorial / financial-document feel — slate page bg, deep
   forest accent, JetBrains Mono for figures + IDs + dates.
   ═══════════════════════════════════════════════════════════ */

export const T = {
  bg: "#f1f5f9",
  surface: "#ffffff",
  surface2: "#f3f5f8",
  surface3: "#f7f9fb",
  ink: "#0f172a",
  ink2: "#334155",
  ink3: "#64748b",
  ink4: "#94a3b8",
  line: "#e4e9f0",
  line2: "#e6ebf2",
  accent: "#1a3d2e",
  accentSoft: "#e8f0eb",
  accentLine: "#c5dccd",
  warn: "#b8641d",
  warnSoft: "#fbf0e2",
  urgent: "#a82a2a",
  urgentSoft: "#fae8e8",
  good: "#2d6a4a",
  goodSoft: "#e3efe7",
  info: "#2c5282",
  infoSoft: "#e6edf5",
  infoLine: "#c7d6e9",
};

export const FONT_SERIF = '"Fraunces", "Source Serif Pro", Georgia, serif';
export const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

export const SHADOW_SM =
  "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 6px rgba(15, 23, 42, 0.05)";
export const SHADOW_MD =
  "0 2px 4px rgba(15, 23, 42, 0.05), 0 6px 16px rgba(15, 23, 42, 0.07)";

/* ═══════════════════════════════════════════════════════════
   STAGE PROGRESSION
   Same canonical sequence the existing redesign uses, but the
   tracker renders thin progress bars (not chevrons) and shows
   a date underneath each stage. We don't have per-stage
   history yet, so reached stages other than the current one
   render with "—" — the slot is reserved so the row reads as
   a mini-timeline rather than a status bar.
   ═══════════════════════════════════════════════════════════ */

// Exported so the drilldown header's LoanStageProgressBar renders the EXACT
// same stage set as this tab's StageTracker (single source of truth — the two
// must never disagree). `abbr` is header-only (compact segment labels);
// StageTracker ignores it.
export const STAGES = [
  // "Prospect" is the loan's initial status server-side (loanStatus default =
  // "Prospect"; "Lead" is not a backend status option). Rendered as
  // "Prospect created" via the first-stage " created" suffix.
  { key: "Prospect", label: "Prospect", abbr: "PROSPECT" },
  { key: "Terms Accepted", label: "Terms accepted", abbr: "TERMS" },
  { key: "Processing", label: "Processing", abbr: "PROC" },
  { key: "Underwriting", label: "Underwriting", abbr: "UW" },
  { key: "Condition Clearance", label: "Cond. clearance", abbr: "COND" },
  { key: "Final Review", label: "Final review", abbr: "REVIEW" },
  { key: "Clear To Close", label: "Clear to close", abbr: "CTC" },
];

export const SUB_TO_PARENT = {
  "Appraisal Ordered": "Processing",
  "Appraisal In": "Processing",
  "Pre-Underwrite": "Processing",
  "Docs Out": "Clear To Close",
  "Docs Back": "Clear To Close",
  "Funded with Post Close Conditions": "Closed",
  Funded: "Closed",
};

// ── Acquisitions (postclose) milestone sequence ──────────────────────────────
// The trade lifecycle from loan-acquisitions.md §1. Linear happy path; the
// off-track terminals (Kicked / Declined) live in
// ACQUISITION_TERMINAL_STATUSES below, mirroring the preclose split. Acquisitions
// has no sub-status enum on the backend, so there's no sub→parent rollup here.
// Inactive (on hold) isn't a stage either: a held loan stays drawn at the stage
// it was held at (acquisitionHeldStatus), marked on hold.
export const ACQUISITION_STAGES = [
  { key: "New Trade", label: "New trade", abbr: "NEW" },
  { key: "Trade Review", label: "Trade review", abbr: "REVIEW" },
  { key: "Due Diligence", label: "Due diligence", abbr: "DD" },
  // owner 2026-10-01: Due Diligence (internal underwriting + TPR) is followed by
  // Condition Clearance → Final Review → Cleared to Purchase.
  { key: "Condition Clearance", label: "Cond. clearance", abbr: "COND" },
  { key: "Final Review", label: "Final review", abbr: "FINAL" },
  { key: "Cleared to Purchase", label: "Cleared to purchase", abbr: "CLEARED" },
  { key: "Funding", label: "Funding", abbr: "FUNDING" },
  { key: "Settled", label: "Settled", abbr: "SETTLED" },
  { key: "Boarded", label: "Boarded", abbr: "BOARDED" },
];

// Stable empty map for pipelines with no sub-statuses (acquisitions) — reused for
// both the sub→parent rollup and the parent→sub-options lookup so the referenced
// object identity stays constant across renders.
const EMPTY_MAP = {};

/**
 * Where a loan sits on its pipeline's stage ladder: `key` is the STAGES /
 * ACQUISITION_STAGES key to mark current, `onHold` is true for an Inactive
 * acquisition drawn at the stage it was held at. Shared by the Summary /
 * Loan Info StageTracker and the drilldown header's LoanStageProgressBar so
 * they can never disagree.
 */
export const resolveStagePosition = (loan, isAcq) => {
  const status = loan?.loanStatus;
  if (isAcq) {
    const held = acquisitionHeldStatus(loan);
    return held ? { key: held, onHold: true } : { key: status, onHold: false };
  }
  return { key: SUB_TO_PARENT[status] || status, onHold: false };
};

/**
 * The acquisition's sub-stage tracking steps for one milestone, from the
 * backend's computed `stageTracking` ({ milestone, step, field, value, done },
 * ACQUISITION_STAGE_STEPS in loan_acquisitions.py). Empty when the milestone
 * tracks no steps or the payload predates the field.
 */
export const acquisitionStageSteps = (loan, milestone) =>
  (Array.isArray(loan?.stageTracking) ? loan.stageTracking : []).filter(
    (s) => s?.milestone === milestone,
  );

/* ═══════════════════════════════════════════════════════════
   SHARED SUMMARY PRIMITIVES
   This module is the primitive library for the loan-drilldown Summary tab
   (LoanDrilldownSummaryTab.js) and the Closer View. It has no tab of its own —
   it only exports design tokens + reusable pieces (Hero, KpiRow, Card,
   AssignedTeamRows, StatusChanger, …).
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   TOP BAR — breadcrumb + secondary actions
   The "Advance stage" primary action lives at the right; one
   primary per page. Export is secondary.
   ═══════════════════════════════════════════════════════════ */

export const TopBar = ({ loan, navigate }) => {
  return (
    <Flx ac jb g={3} wrap sx={{ mb: 3 }}>
      <Flx ac g={1}>
        <BreadLink onClick={() => navigate("/loans")}>Loans</BreadLink>
        <Sep />
        <BreadLink onClick={() => navigate("/loans")}>
          Active pipeline
        </BreadLink>
        <Sep />
        <Txt
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            color: T.ink,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          #{loan?.loanNumber || "—"}
        </Txt>
      </Flx>
    </Flx>
  );
};

const BreadLink = ({ children, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 12,
      color: T.ink3,
      cursor: "pointer",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      "&:hover": { color: T.ink },
    }}
  >
    {children}
  </Box>
);

const Sep = () => (
  <Txt sx={{ color: T.ink4, fontSize: 12, fontFamily: FONT_MONO }}>/</Txt>
);

/* ═══════════════════════════════════════════════════════════
   ZONE 1 — HERO
   Identity (title + breadcrumb-style sub strip), status pill
   that doubles as the status changer, and the stage tracker
   rendered as thin progress bars with dates beneath each.
   ═══════════════════════════════════════════════════════════ */

export const Hero = ({ loan }) => {
  const firstAddress =
    addressLabel(loan?.subjectProperties?.[0]?.address) || null;
  const firstBorrower = loan?.borrowers?.[0];
  const borrowerName = firstBorrower
    ? `${firstBorrower?.firstName || ""} ${firstBorrower?.lastName || ""}`.trim()
    : null;

  const title = useMemo(() => {
    if (loan?.loanName) return loan.loanName;
    const parts = [borrowerName, firstAddress].filter(Boolean);
    return parts.length ? parts.join(" — ") : `Loan #${loan?.loanNumber}`;
  }, [loan?.loanName, loan?.loanNumber, borrowerName, firstAddress]);

  const lastUpdatedRel = useMemo(() => {
    if (!loan?.updateDatetime) return null;
    const ms = Date.now() - timestampMs(loan.updateDatetime);
    if (Number.isNaN(ms)) return null;
    const days = Math.round(ms / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  }, [loan?.updateDatetime]);

  return (
    <Box
      sx={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "12px",
        boxShadow: "none",
        p: { xs: "22px", md: "28px 32px" },
        pb: "8px !important",
      }}
    >
      <Flx wrap g={3} jb sx={{ alignItems: "flex-start", mb: "28px" }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Txt
            component="h1"
            sx={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: 28,
              letterSpacing: "-0.02em",
              m: 0,
              mb: 1,
              color: T.ink,
              lineHeight: 1.15,
            }}
          >
            {title}
          </Txt>
          <Flx ac g={1.25} wrap>
            <IdChip>#{loan?.loanNumber || "—"}</IdChip>
            {loan?.loanProductType && <Dot />}
            {loan?.loanProductType && (
              <ProductTag>{loan.loanProductType}</ProductTag>
            )}
            {loan?.loanPurpose && <Dot />}
            {loan?.loanPurpose && (
              <Txt sx={{ fontSize: 13, color: T.ink3 }}>{loan.loanPurpose}</Txt>
            )}
            {loan?.createDateTime && <Dot />}
            {loan?.createDateTime && (
              <Txt sx={{ fontSize: 13, color: T.ink3 }}>
                Created {formatDateTime(loan.createDateTime, "MMMM d, yyyy")}
                {lastUpdatedRel ? ` · last updated ${lastUpdatedRel}` : ""}
              </Txt>
            )}
          </Flx>
        </Box>

        <StatusChanger loan={loan} />
      </Flx>

      <StageTracker loan={loan} />

      <HeroInsights loan={loan} />
    </Box>
  );
};

/* ─── Hero insights (expandable) ────────────────────────────
   A centered arrow pinned to the bottom of the overview card. Clicking it
   reveals: a days-per-stage bar chart, a detailed stage timeline (who moved
   it + how long it sat there), and the most recent loan events — all derived
   from the same milestoneStatusChanges / loan-events feed the rest of the tab
   uses. */

// Rotating palette for stage bars / timeline dots (indexed by STAGES position
// so a stage keeps its color between the chart and the timeline).
const STAGE_PALETTE = [
  "#64748b",
  "#2c5282",
  "#7c5cbf",
  "#0f766e",
  "#b45309",
  "#be185d",
  "#2d6a4a",
  "#475569",
  "#a82a2a",
  "#0e7490",
];

const stageColor = (statusKey, subToParent) => {
  const parent = subToParent[statusKey] || statusKey;
  const idx = STAGES.findIndex((s) => s.key === parent);
  const acqIdx = ACQUISITION_STAGES.findIndex((s) => s.key === parent);
  const i = idx >= 0 ? idx : acqIdx >= 0 ? acqIdx : 0;
  return STAGE_PALETTE[i % STAGE_PALETTE.length];
};

const DAY_MS = 86_400_000;
const fmtDays = (ms) => {
  if (!ms || ms <= 0) return "—";
  const d = ms / DAY_MS;
  if (d < 1) {
    const h = Math.max(1, Math.round(ms / 3_600_000));
    return `${h}h`;
  }
  return `${d < 10 ? d.toFixed(1) : Math.round(d)} days`;
};

// Chronological milestone changes → timeline rows with per-status duration
// (entry until the next change, or "now" for the current status).
const buildStageTimeline = (loan) => {
  const rows = (loan?.milestoneStatusChanges || [])
    .map((c) => ({
      status: c?.updatedMilestoneStatus,
      by: c?.updateBy,
      t: c?.milestoneChangeDate
        ? timestampMs(c.milestoneChangeDate)
        : null,
    }))
    .filter((c) => c.status && c.t != null && !Number.isNaN(c.t))
    .sort((a, b) => a.t - b.t);
  const now = Date.now();
  return rows.map((c, i) => ({
    ...c,
    durationMs: Math.max(0, (i + 1 < rows.length ? rows[i + 1].t : now) - c.t),
    isCurrent: i === rows.length - 1,
  }));
};

const HeroInsights = ({ loan }) => {
  const [open, setOpen] = useState(false);
  const isAcq = useIsAcquisitions();
  const subToParent = isAcq ? EMPTY_MAP : SUB_TO_PARENT;

  const timeline = useMemo(() => buildStageTimeline(loan), [loan]);

  // Roll per-status durations up to their parent stage for the bar chart.
  // Kept for the standalone DaysPerStageChart (not currently rendered — the
  // combined StageTimelineDuration owns the expanded view).
  // eslint-disable-next-line no-unused-vars
  const stageBars = useMemo(() => {
    const totals = {};
    for (const item of timeline) {
      const parent = subToParent[item.status] || item.status;
      totals[parent] = (totals[parent] || 0) + item.durationMs;
    }
    const stages = isAcq ? ACQUISITION_STAGES : STAGES;
    // Include EVERY stage — statuses the loan hasn't reached (or where the time
    // can't be bounded) show a 0-length bar so the full pipeline is visible.
    const rows = stages.map((s) => ({
      key: s.key,
      label: s.label,
      ms: totals[s.key] || 0,
      color: stageColor(s.key, subToParent),
    }));
    const max = rows.reduce((m, r) => Math.max(m, r.ms), 0);
    return { rows, max };
  }, [timeline, subToParent, isAcq]);

  const hasData = timeline.length > 0;
  // Even with no logged milestone transitions, the loan still has a creation
  // moment — render the ladder anchored to `createDateTime` (the "Prospect
  // created" first stage, derived by computeStageLadder) instead of the empty
  // placeholder. Only fall back to the placeholder when there's no date at all.
  const hasStageData = hasData || !!loan?.createDateTime;
  // Hide the whole "Stage timeline & activity" affordance when the loan has
  // nothing to show. Comments / uploaded documents count as activity too, so
  // the panel still appears for loans with activity but no milestone changes.
  // Kept to in-memory loan fields to preserve the lazy events fetch.
  const hasAnyActivity =
    hasData || size(loan?.comments) > 0 || size(loan?.loanDocuments) > 0;

  if (!hasAnyActivity) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Center toggle arrow, pinned to the bottom of the card. */}
      <Flx center sx={{ mt: 3 }}>
        <Box
          role="button"
          onClick={() => setOpen((o) => !o)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            px: "12px",
            py: "5px",
            borderRadius: "999px",
            border: `1px solid ${T.line}`,
            background: T.surface3,
            color: T.ink3,
            zIndex: 111,
            cursor: "pointer",
            transition: "background .15s, color .15s, border-color .15s",
            "&:hover": { background: T.surface2, color: T.ink2 },
          }}
        >
          <Txt
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "#2c5282",
            }}
          >
            {open ? "Hide details" : "Stage timeline & activity"}
          </Txt>
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 18,
              transition: "transform .2s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </Box>
      </Flx>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            // mt: 4,
            // pt: 4,
            // pb: 3,
            //
            borderTop: `1px solid ${T.line2}`,
            mt: "-14px",
          }}
        >
          {/* Combined stage timeline + duration on the left, recent activity on
              the right. Recent activity renders even when there's no stage
              history so field edits / uploads / comments still show. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 4px 1fr" },
              // gap: { xs: "32px", md: 9 },
              // gap: { xs: "32px", md: "48px" },
            }}
          >
            <Box sx={{ minWidth: 0, p: 4, pt: 4.5, pl: 0 }}>
              {hasStageData ? (
                <StageTimelineDuration
                  timeline={timeline}
                  subToParent={subToParent}
                  loan={loan}
                  stages={isAcq ? ACQUISITION_STAGES : STAGES}
                />
              ) : (
                <>
                  <StageTimelineHeader />
                  <Txt
                    sx={{
                      fontSize: 12.5,
                      color: T.ink3,
                      fontStyle: "italic",
                      py: "8px",
                    }}
                  >
                    No stage history recorded for this loan yet.
                  </Txt>
                </>
              )}
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", md: "block" } }}
            />
            <Box sx={{ minWidth: 0, p: 4, pt: 4.5, pr: 0 }}>
              <RecentEventsList loan={loan} active={open} />
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const InsightHeader = ({ icon, label, endContent }) => (
  <Flx ac g={0.75} sx={{ mb: "12px" }}>
    <Box
      sx={{
        color: T.ink3,
        display: "flex",
        ".MuiSvgIcon-root": { fontSize: 16 },
      }}
    >
      {icon}
    </Box>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: T.ink3,
      }}
    >
      {label}
    </Txt>
    {endContent && <Box sx={{ ml: "auto" }}>{endContent}</Box>}
  </Flx>
);

// eslint-disable-next-line no-unused-vars
const DaysPerStageChart = ({ rows, max }) => (
  <Flx column g={2}>
    {rows.map((r) => {
      const pct = r.ms > 0 && max > 0 ? Math.max(3, (r.ms / max) * 100) : 0;
      return (
        <Box key={r.key}>
          <Flx ac jb sx={{ mb: "3px" }}>
            <Txt
              sx={{
                fontSize: 11.5,
                fontWeight: 500,
                color: T.ink2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.label}
            </Txt>
            <Txt
              sx={{
                fontSize: 11,
                fontFamily: FONT_MONO,
                color: T.ink3,
                flexShrink: 0,
                ml: 1,
              }}
            >
              {fmtDays(r.ms)}
            </Txt>
          </Flx>
          <Box sx={{ height: 14, borderRadius: "4px", background: T.surface2 }}>
            <Box
              sx={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: "4px",
                background: r.color,
                transition: "width .4s",
              }}
            />
          </Box>
        </Box>
      );
    })}
  </Flx>
);

// Stage-ladder tokens (brighter than the editorial page tokens to read like the
// reference mockup: green = done, blue = current, gray = upcoming).
const LADDER = {
  done: "#16a34a",
  current: "#2563eb",
  currentBg: "#eef4ff",
  currentLine: "#c9dbff",
  future: "#c2ccd3",
  amber: "#d97706",
  amberBg: "#fdf0e1",
  ink: "#16242b",
  sub: "#5d7079",
  faint: "#98a4ac",
};

const longMonthDay = (ms) =>
  formatDateTime(new Date(ms).toISOString(), "MMMM d");
// Exact date + time for the stage time frame (Eastern, via formatDateTime).
const fmtDT = (ms) =>
  formatDateTime(new Date(ms).toISOString(), "MMM d, h:mm a");

// Resolve who moved the stage; integration-driven changes collapse to a single
// "Salesforce sync" label like the mockup.
const resolveMover = (by, users) => {
  if (!by) return null;
  if (/sync|salesforce|system|integration|service|automation/i.test(by))
    return "Salesforce sync";
  return getUserFullNameFromEmail(users, by) || by.split("@")[0];
};

// Segmented progress bar: one pill per canonical stage.
const StageProgressBar = ({ stages, currentIdx }) => (
  <Flx g={0.5} sx={{ mb: "16px" }}>
    {stages.map((s, i) => {
      const bg =
        i < currentIdx
          ? LADDER.done
          : i === currentIdx
            ? LADDER.current
            : "#e4e9ee";
      return (
        <Box
          key={s.key}
          sx={{ flex: 1, height: 5, borderRadius: "999px", background: bg }}
        />
      );
    })}
  </Flx>
);

// Build the canonical stage ladder (state / dates / duration / actor per stage)
// from the logged milestone timeline. Shared by the standalone StageTimelineList
// and the combined StageTimelineDuration so both stay in sync.
const computeStageLadder = ({ timeline, subToParent, loan, stages }) => {
  const createdT = loan?.createDateTime
    ? timestampMs(loan.createDateTime)
    : null;
  const now = Date.now();

  // Roll each logged transition up to its parent stage; keep the FIRST entry
  // time + actor and accumulate the time spent in that stage.
  const byStage = {};
  for (const item of timeline) {
    const parent = subToParent[item.status] || item.status;
    const rec = byStage[parent];
    if (!rec) {
      byStage[parent] = {
        enteredT: item.t,
        by: item.by,
        durationMs: item.durationMs || 0,
      };
    } else {
      if (item.t < rec.enteredT) {
        rec.enteredT = item.t;
        rec.by = item.by;
      }
      rec.durationMs += item.durationMs || 0;
    }
  }

  // The current stage = parent of the most recent transition (fallback: the
  // first canonical stage if nothing is logged yet).
  const lastItem = timeline[timeline.length - 1];
  const currentKey = lastItem
    ? subToParent[lastItem.status] || lastItem.status
    : stages[0]?.key;
  let currentIdx = stages.findIndex((s) => s.key === currentKey);
  if (currentIdx < 0) currentIdx = stages.length; // terminal (Closed/Dead)

  // enteredT per reached stage, for range end-dates.
  const enteredByIdx = stages.map((s, i) => {
    const rec = byStage[s.key];
    if (rec) return rec.enteredT;
    if (i === 0 && createdT != null) return createdT; // Lead = loan creation
    return null;
  });
  const nextReachedT = (i) => {
    for (let j = i + 1; j < stages.length; j++)
      if (enteredByIdx[j] != null) return enteredByIdx[j];
    return null;
  };

  const built = stages.map((s, i) => {
    const rec = byStage[s.key];
    const enteredT = enteredByIdx[i];
    const state =
      i < currentIdx ? "done" : i === currentIdx ? "current" : "future";
    let durationMs = rec?.durationMs || 0;
    if (i === 0 && !rec && enteredT != null) {
      // Lead has no explicit transition: time from creation to the next stage.
      const nxt = nextReachedT(0);
      durationMs = Math.max(0, (nxt ?? now) - enteredT);
    }
    if (state === "current" && enteredT != null)
      durationMs = Math.max(0, now - enteredT);
    return {
      key: s.key,
      idx: i,
      label: i === 0 ? `${s.label} created` : s.label,
      state,
      enteredT,
      exitT: nextReachedT(i),
      durationMs,
      by: rec?.by ?? (i === 0 ? loan?.createdBy : null),
      isLast: i === stages.length - 1,
    };
  });

  // Mark the longest completed stage for the "longest stage" chip.
  let longestIdx = -1;
  let longestMs = 0;
  built.forEach((r, i) => {
    if (r.state === "done" && r.durationMs > longestMs) {
      longestMs = r.durationMs;
      longestIdx = i;
    }
  });
  if (longestIdx >= 0) built[longestIdx].isLongest = true;

  return { built, currentIdx };
};

// Compact duration: minutes < 1h, hours < 1 day, else days. "—" for none.
const fmtDur = (ms) => {
  if (!ms || ms <= 0) return "—";
  const mins = ms / 60000;
  if (mins < 60) return `${Math.max(1, Math.round(mins))}m`;
  const hrs = ms / 3_600_000;
  if (hrs < 24) return `${Math.round(hrs)}h`;
  const d = ms / 86_400_000;
  return `${d < 10 ? d.toFixed(1) : Math.round(d)} days`;
};

// Exact date line for a stage row (shared by the list + combined views).
const stageDateLine = (r, closingT) => {
  if (r.enteredT != null) {
    const start = fmtDT(r.enteredT);
    if (r.state === "current") return `Started ${start}`;
    if (r.state === "done" && r.exitT != null)
      return `${start} → ${fmtDT(r.exitT)}`;
    return start;
  }
  if (r.state === "future" && r.isLast && closingT != null) {
    const days = daysUntilDateOnly(new Date(closingT));
    const away =
      days > 0
        ? ` · ${days} day${days === 1 ? "" : "s"} away`
        : days < 0
          ? ` · ${Math.abs(days)} day${days === -1 ? "" : "s"} overdue`
          : " · due today";
    return `Target ${longMonthDay(closingT)}${away}`;
  }
  return null;
};

// eslint-disable-next-line no-unused-vars
const StageTimelineList = ({ timeline, subToParent, loan, stages }) => {
  const users = useSelector((state) => state.users);

  const rows = useMemo(
    () => computeStageLadder({ timeline, subToParent, loan, stages }),
    [timeline, subToParent, loan?.createDateTime, loan?.createdBy, stages],
  );

  const closingT = loan?.expectedClosingDate
    ? dateOnlyMs(loan.expectedClosingDate)
    : null;

  return (
    <Box>
      <StageProgressBar stages={stages} currentIdx={rows.currentIdx} />

      {rows.built.map((r) => {
        const mover = resolveMover(r.by, users);
        // Exact time frame (date + time via formatDateTime): a start → end
        // range for completed stages, "Started …" for the current stage, and a
        // "Target …" closing date for the final upcoming stage.
        let dateLine = null;
        if (r.enteredT != null) {
          const start = fmtDT(r.enteredT);
          if (r.state === "current") dateLine = `Started ${start}`;
          else if (r.state === "done" && r.exitT != null)
            dateLine = `${start} → ${fmtDT(r.exitT)}`;
          else dateLine = start;
        } else if (r.state === "future" && r.isLast && closingT != null) {
          const days = daysUntilDateOnly(new Date(closingT));
          const away =
            days > 0
              ? ` · ${days} day${days === 1 ? "" : "s"} away`
              : days < 0
                ? ` · ${Math.abs(days)} day${days === -1 ? "" : "s"} overdue`
                : " · due today";
          dateLine = `Target ${longMonthDay(closingT)}${away}`;
        }

        const isCurrent = r.state === "current";
        return (
          <Flx
            key={r.key}
            g={1.25}
            sx={{
              alignItems: "flex-start",
              px: isCurrent ? "10px" : 0,
              mx: isCurrent ? "-10px" : 0,
              py: "9px",
              borderRadius: "10px",
              background: isCurrent ? LADDER.currentBg : "transparent",
              border: `1px solid ${isCurrent ? LADDER.currentLine : "transparent"}`,
            }}
          >
            {/* status circle */}
            <Box sx={{ flexShrink: 0, mt: "1px" }}>
              {r.state === "done" ? (
                <CheckCircleOutlineRounded
                  sx={{ fontSize: 19, color: LADDER.done }}
                />
              ) : (
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: `2px dashed ${isCurrent ? LADDER.current : LADDER.future}`,
                  }}
                />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Flx ac g={0.75} jb sx={{ gap: "8px" }}>
                <Flx ac g={0.75} wrap sx={{ minWidth: 0 }}>
                  <Txt
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: r.state === "future" ? LADDER.faint : LADDER.ink,
                    }}
                  >
                    {r.label}
                  </Txt>
                  {r.isLongest && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: LADDER.amber,
                        background: LADDER.amberBg,
                        px: "7px",
                        py: "1px",
                        borderRadius: "999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      longest stage
                    </Box>
                  )}
                  {isCurrent && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: LADDER.current,
                        border: `1px solid ${LADDER.currentLine}`,
                        px: "7px",
                        py: "1px",
                        borderRadius: "999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      current
                    </Box>
                  )}
                </Flx>
              </Flx>
              {dateLine && (
                <Txt
                  sx={{
                    fontSize: 11.5,
                    color: isCurrent ? LADDER.current : LADDER.faint,
                    mt: "2px",
                  }}
                >
                  {dateLine}
                  {mover ? ` · ${mover}` : ""}
                </Txt>
              )}
            </Box>
          </Flx>
        );
      })}
    </Box>
  );
};

// Small status circle: green check (done), dashed blue (current), dashed gray
// (upcoming).
const StageCircle = ({ state }) =>
  state === "done" ? (
    <CheckCircleOutlineRounded sx={{ fontSize: 19, color: LADDER.done }} />
  ) : (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `2px dashed ${state === "current" ? LADDER.current : LADDER.future}`,
      }}
    />
  );

// Combined "Stage timeline and duration": the stage ladder (state + exact time
// frame) with a per-stage duration bar and value, plus a top progress bar and a
// "N of M stages complete" header. Merges what the standalone Days-per-stage +
// Stage-timeline panels showed into one row per stage.
// Shared "STAGE TIMELINE AND DURATION" header (icon + label + optional
// right-aligned end content). Rendered by StageTimelineDuration AND the
// empty-state fallback so the section header shows even with no stage history.
const StageTimelineHeader = ({ endContent }) => (
  <Flx ac jb g={1} sx={{ mb: "12px" }}>
    <Flx ac g={0.75}>
      <TimelineOutlined sx={{ fontSize: 16, color: T.ink3 }} />
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: T.ink3,
        }}
      >
        Stage timeline and duration
      </Txt>
    </Flx>
    {endContent}
  </Flx>
);

const StageTimelineDuration = ({ timeline, subToParent, loan, stages }) => {
  const users = useSelector((state) => state.users);
  const { built, currentIdx } = useMemo(
    () => computeStageLadder({ timeline, subToParent, loan, stages }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeline, subToParent, loan?.createDateTime, loan?.createdBy, stages],
  );

  const closingT = loan?.expectedClosingDate
    ? dateOnlyMs(loan.expectedClosingDate)
    : null;
  const maxDur = built.reduce((m, r) => Math.max(m, r.durationMs || 0), 0);
  const doneCount = built.filter((r) => r.state === "done").length;

  return (
    <Box>
      <StageTimelineHeader
        endContent={
          <Txt
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: T.ink4,
              whiteSpace: "nowrap",
            }}
          >
            {doneCount} of {stages.length} stages complete
          </Txt>
        }
      />

      {/* <StageProgressBar stages={stages} currentIdx={currentIdx} /> */}

      <Box>
        {built.map((r) => {
          const isCurrent = r.state === "current";
          const dateLine = stageDateLine(r, closingT);
          const mover = resolveMover(r.by, users);
          const barColor =
            r.state === "future" ? "#e4e9ee" : stageColor(r.key, subToParent);
          const pct =
            maxDur > 0 && r.durationMs > 0
              ? Math.max(5, (r.durationMs / maxDur) * 100)
              : 0;
          const durLabel =
            r.state === "current"
              ? `${fmtDur(r.durationMs)}+`
              : r.state === "future"
                ? "—"
                : fmtDur(r.durationMs);
          return (
            <Flx
              key={r.key}
              g={1.25}
              sx={{
                // alignItems: "center",
                alignItems: "flex-start",
                px: isCurrent ? "10px" : 0,
                mx: isCurrent ? "-10px" : 0,
                py: "9px",
                borderRadius: "10px",
                background: isCurrent ? LADDER.currentBg : "transparent",
                border: `1px solid ${isCurrent ? LADDER.currentLine : "transparent"}`,
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  pt: 0.5,
                }}
              >
                <StageCircle state={r.state} />
              </Box>

              {/* name + chips + exact time frame */}
              <Box sx={{ flex: "1.3 1 0", minWidth: 0 }}>
                <Flx ac g={0.75} wrap>
                  <Txt
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: r.state === "future" ? LADDER.faint : LADDER.ink,
                    }}
                  >
                    {r.label}
                  </Txt>
                  {r.isLongest && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: LADDER.amber,
                        background: LADDER.amberBg,
                        px: "7px",
                        py: "1px",
                        borderRadius: "999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      longest stage
                    </Box>
                  )}
                  {isCurrent && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: LADDER.current,
                        border: `1px solid ${LADDER.currentLine}`,
                        px: "7px",
                        py: "1px",
                        borderRadius: "999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      current
                    </Box>
                  )}
                </Flx>
                {/* Show the date subline whenever the stage has been reached.
                    Reached stages with no recorded timestamp fall back to a
                    "Date not available" note; future stages stay blank. */}
                {(dateLine || r.state !== "future") && (
                  <Txt
                    sx={{
                      fontSize: 11.5,
                      color: isCurrent ? LADDER.current : LADDER.faint,
                      fontStyle: dateLine ? "normal" : "italic",
                      mt: "2px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateLine || "Date not available"}
                    {mover ? ` · ${mover}` : ""}
                  </Txt>
                )}
              </Box>

              {/* duration bar */}
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 48,
                  height: 10,
                  mt: 0.5,
                  borderRadius: "999px",
                  background: "#eceff3",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: "100%",
                    borderRadius: "999px",
                    background: barColor,
                    transition: "width .4s",
                  }}
                />
              </Box>

              {/* duration value */}
              <Txt
                sx={{
                  width: 56,
                  flexShrink: 0,
                  textAlign: "right",
                  fontSize: 11.5,
                  fontFamily: FONT_MONO,
                  fontWeight: 500,
                  color: isCurrent
                    ? LADDER.current
                    : r.state === "future"
                      ? LADDER.faint
                      : LADDER.sub,
                }}
              >
                {durLabel}
              </Txt>
            </Flx>
          );
        })}
      </Box>
    </Box>
  );
};

// The most recent loan events — fetched only when the panel is opened. Uses the
// same loanChangelogConfig helpers as the changelog sidebar so the wording +
// icons stay consistent, but renders an avatar-led feed (see ActivityFeedItem).
const isViewEventSummary = (ev) =>
  ev?.event_type === "loan_view" ||
  ev?.sub_event === "view_file" ||
  // Read-only pulls (GET endpoints, e.g. get_loan_terms) are views, not edits.
  /^get_/i.test(ev?.sub_event || "");

// Day label for a grouped feed: "Today" / "Yesterday" / "Jul 14".
const feedDayLabel = (iso) => {
  // Raw backend ISO goes straight in — audit timestamps are naive UTC and
  // formatDateTime appends the 'Z'. `new Date(iso)` would read them as LOCAL.
  const key = formatDateTime(iso, "yyyy-MM-dd");
  const today = formatDateTime(new Date().toISOString(), "yyyy-MM-dd");
  const yday = formatDateTime(
    new Date(Date.now() - 86400000).toISOString(),
    "yyyy-MM-dd",
  );
  if (key === today) return "Today";
  if (key === yday) return "Yesterday";
  return formatDateTime(iso, "MMM d");
};

const RecentEventsList = ({ loan, active }) => {
  const { getLoanEvents } = useUnderwritingHook();
  const dispatch = useDispatch();
  const dealId = loan?._id;
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(null);

  useEffect(() => {
    if (!active || isNil(dealId) || fetchedRef.current === dealId)
      return undefined;
    fetchedRef.current = dealId;
    let alive = true;
    setLoading(true);
    getLoanEvents({
      dealId,
      onSuccessFn: (data) => alive && setEvents(data?.events || []),
      onCompleteFn: () => alive && setLoading(false),
      onFailFn: () => alive && setEvents([]),
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, dealId]);

  // Same filtering as the changelog sidebar: drop view-only noise, newest first.
  const changes = useMemo(
    () =>
      (events || [])
        .filter((ev) => !isViewEventSummary(ev))
        .sort((a, b) =>
          String(b.timestamp || "").localeCompare(String(a.timestamp || "")),
        ),
    [events],
  );
  const SHOWN = 5;
  const recent = useMemo(() => changes.slice(0, SHOWN), [changes]);
  const loanDocuments = loan?.loanDocuments || [];

  // Group the shown items by day (like the Activity Log sidebar).
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const ev of recent) {
      const key = formatDateTime(
        new Date(ev.timestamp).toISOString(),
        "yyyy-MM-dd",
      );
      if (!cur || cur.key !== key) {
        cur = { key, iso: ev.timestamp, items: [] };
        out.push(cur);
      }
      cur.items.push(ev);
    }
    return out;
  }, [recent]);

  const openLog = () =>
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.LOAN_CHANGE_HISTORY));
  // Open the document in the app's persistent file-preview sidebar. When a
  // right-docked drawer is open it docks on the left (GlobalLoanFilePreviewSidebar).
  const handlePreview = (doc) => dispatch(filePreviewSetData(doc));

  return (
    <Box>
      <InsightHeader
        icon={<HistoryOutlined />}
        label={`Recent activity${changes.length ? ` (${changes.length})` : ""}`}
        endContent={
          changes.length > SHOWN ? (
            <Box
              role="button"
              onClick={openLog}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: "#94a3b8",
                // color: T.info,
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View more →
            </Box>
          ) : null
        }
      />
      {loading && !events ? (
        <Txt sx={{ fontSize: 12.5, color: T.ink3 }}>Loading activity…</Txt>
      ) : recent.length === 0 ? (
        <Txt sx={{ fontSize: 12.5, color: T.ink3, fontStyle: "italic" }}>
          No recent activity.
        </Txt>
      ) : (
        <Box sx={{ py: "5px" }}>
          {groups.map((g, gi) => (
            <Box key={g.key}>
              {/* Day header — only meaningful once the 5 span multiple days,
                  but always shown so the grouping reads clearly. */}
              <Txt
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: T.ink4,
                  mt: gi === 0 ? 0 : "10px",
                  mb: "2px",
                }}
              >
                {feedDayLabel(g.iso)}
              </Txt>
              {g.items.map((ev, i) => (
                <ActivityFeedItem
                  key={`${ev.timestamp}-${i}`}
                  ev={ev}
                  loanDocuments={loanDocuments}
                  onPreview={handlePreview}
                  isLast={gi === groups.length - 1 && i === g.items.length - 1}
                />
              ))}
            </Box>
          ))}
          {changes?.length > SHOWN ? (
            <Button
              variant="text"
              // color="info"
              fullWidth
              onClick={openLog}
              sx={{
                // fontSize: 11,
                fontWeight: 700,
                // color: T.info,
                // m: "0 auto",
                // width: "100%",
                mt: 2,
                // cursor: "pointer",
                // whiteSpace: "nowrap",
                // "&:hover": { textDecoration: "underline" },
              }}
            >
              View more →
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

const IdChip = ({ children }) => (
  <Txt
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 12,
      color: T.ink2,
      background: T.surface2,
      px: 1,
      py: "2px",
      borderRadius: "4px",
      border: `1px solid ${T.line2}`,
    }}
  >
    {children}
  </Txt>
);

const ProductTag = ({ children }) => (
  <Txt
    sx={{
      background: T.infoSoft,
      color: T.info,
      border: `1px solid ${T.infoLine}`,
      px: 1,
      py: "2px",
      borderRadius: "4px",
      fontSize: 11,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      fontFamily: FONT_MONO,
    }}
  >
    {children}
  </Txt>
);

const Dot = () => (
  <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: T.ink4 }} />
);

/* ─── Status pill = status changer ──────────────────────────
   The tracker reflects the change visually; the pill is what
   the user actually clicks. Keeping these merged means there
   is one source of truth for the loan's current state.
*/

const STATUS_TONE = {
  Prospect: { fg: T.ink2, bg: T.surface2, line: T.line },
  "Terms Accepted": { fg: T.info, bg: T.infoSoft, line: T.infoLine },
  Processing: { fg: T.info, bg: T.infoSoft, line: T.infoLine },
  Underwriting: { fg: T.info, bg: T.infoSoft, line: T.infoLine },
  "Condition Clearance": { fg: T.warn, bg: T.warnSoft, line: "#e6c8a3" },
  "Final Review": { fg: T.warn, bg: T.warnSoft, line: "#e6c8a3" },
  "Clear To Close": { fg: T.good, bg: T.goodSoft, line: "#bcdcc7" },
  Closed: { fg: T.good, bg: T.goodSoft, line: "#bcdcc7" },
  Inactive: { fg: T.urgent, bg: T.urgentSoft, line: "#e8c4c4" },
  Dead: { fg: T.urgent, bg: T.urgentSoft, line: "#e8c4c4" },
  // ── Acquisitions (postclose) milestones ──
  "New Trade": { fg: T.ink2, bg: T.surface2, line: T.line },
  "Trade Review": { fg: T.info, bg: T.infoSoft, line: T.infoLine },
  "Due Diligence": { fg: T.info, bg: T.infoSoft, line: T.infoLine },
  "Cleared to Purchase": { fg: T.warn, bg: T.warnSoft, line: "#e6c8a3" },
  Funding: { fg: T.warn, bg: T.warnSoft, line: "#e6c8a3" },
  Settled: { fg: T.good, bg: T.goodSoft, line: "#bcdcc7" },
  Boarded: { fg: T.good, bg: T.goodSoft, line: "#bcdcc7" },
  // Condition Clearance / Final Review / Inactive share the preclose tones above.
  Kicked: { fg: T.urgent, bg: T.urgentSoft, line: "#e8c4c4" },
  Declined: { fg: T.urgent, bg: T.urgentSoft, line: "#e8c4c4" },
};

/* Terminal states. They sit below a divider because they take the loan out of
   the normal stage progression. Inactive is a plain milestone write like any
   other status; Dead and Closed each open a modal instead, because both need
   extra data (reason/notes, closing date) and both go to their own endpoint. */
const TERMINAL_STATUSES = [
  {
    key: "Inactive",
    label: "Inactive",
    icon: PauseCircleOutlineRounded,
    opensModal: false,
  },
  {
    key: "Dead",
    label: "Dead…",
    icon: HighlightOffRounded,
    opensModal: true,
  },
  {
    key: "Closed",
    label: "Closed…",
    icon: CheckCircleOutlineRounded,
    opensModal: true,
  },
];

/* Acquisition off-track terminals. Unlike preclose, none open a modal — a
   purchased loan has no "closing date"/"dead reason" capture, so Kicked /
   Declined are plain milestone writes to
   /loan_acquisitions/update_milestone. Inactive is "on hold" (synced to the SF
   On Hold flag, the SF stage is left alone); it's listed here as "Put on hold"
   and the changer offers "Resume" back to the held-at stage while it's set. */
const ACQUISITION_TERMINAL_STATUSES = [
  {
    key: ACQUISITION_ON_HOLD_STATUS,
    label: "Put on hold (Inactive)",
    icon: PauseCircleOutlineRounded,
    opensModal: false,
  },
  {
    key: "Kicked",
    label: "Kicked",
    icon: HighlightOffRounded,
    opensModal: false,
  },
  {
    key: "Declined",
    label: "Declined",
    icon: HighlightOffRounded,
    opensModal: false,
  },
];

export const StatusChanger = ({ loan, compact = false }) => {
  const dispatch = useDispatch();
  const { loading, updateLoanStatusOrSubStatus } = useUnderwritingHook();
  const isAcq = useIsAcquisitions();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  // "Closed" | "Dead" | null — which terminal modal is showing.
  const [modal, setModal] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);
  const [draftSub, setDraftSub] = useState(null);

  const tone = STATUS_TONE[loan?.loanStatus] || STATUS_TONE.Prospect;

  useEffect(() => {
    setDraftStatus(loan?.loanStatus);
    setDraftSub(loan?.subStatus);
  }, [loan?.loanStatus, loan?.subStatus]);

  // Live loan-status options from the backend model schema, falling back to the
  // static constant when the schema hasn't loaded / lacks them. Acquisitions
  // (postclose) has its own milestone enum and NO acquisitions model schema is
  // served (retrieve_object_models.py only maps 'underwriting'), so use the
  // mirrored ACQUISITION_STATUSES constant instead of the model schema.
  const modelStatusOptions = useModelFieldOptions(
    "loanStatus",
    selectOptionsLoanStatus,
  );
  const statusOptions = isAcq ? ACQUISITION_STATUSES : modelStatusOptions;

  // Terminal statuses + sub-status map both differ by pipeline.
  const terminalStatuses = isAcq
    ? ACQUISITION_TERMINAL_STATUSES
    : TERMINAL_STATUSES;
  const terminalKeys = useMemo(
    () => terminalStatuses.map((t) => t.key),
    [terminalStatuses],
  );
  const subStatusMap = isAcq ? EMPTY_MAP : loanSubStatusOptionsMap;
  // An on-hold acquisition resumes at the status its held-at SF stage maps to.
  const heldStatus = isAcq ? acquisitionHeldStatus(loan) : null;

  // The stacked list is every status except the terminal ones. "Prospect" is
  // the loan's initial status server-side (loanStatus default) but is NOT in
  // the model's `loan_status_options` enum, so the schema never returns it —
  // pin it to the top of the underwriting list so a loan can be moved back to
  // it. A current status that lives in neither group (a legacy value like
  // "Closed w/ Post Closing Conditions" or "Loan Sold") is appended last so the
  // dropdown can still show what the loan is actually on.
  const stageOptions = useMemo(() => {
    const list = (statusOptions || []).filter((s) => !terminalKeys.includes(s));
    if (!isAcq && !list.includes("Prospect")) list.unshift("Prospect");
    const current = loan?.loanStatus;
    if (current && !list.includes(current) && !terminalKeys.includes(current)) {
      list.push(current);
    }
    return list;
  }, [statusOptions, terminalKeys, loan?.loanStatus, isAcq]);

  const subOptions = useMemo(
    () => subStatusMap[draftStatus] || [],
    [subStatusMap, draftStatus],
  );
  // A status may offer sub-statuses, but picking one is OPTIONAL — e.g. the user
  // can move to "Clear To Close" without choosing "Docs Out" / "Docs Back".
  const hasSubOptions = !isEmpty(subOptions);
  const isModified =
    draftStatus !== loan?.loanStatus || draftSub !== loan?.subStatus;
  const canSave = isModified;

  const closePanel = useCallback(() => {
    setOpen(false);
    setDraftStatus(loan?.loanStatus);
    setDraftSub(loan?.subStatus);
  }, [loan?.loanStatus, loan?.subStatus]);

  // Switching the parent status drops the sub-status, unless we've landed back
  // on the loan's real status — then restore the one it already has.
  const handleSelectStatus = useCallback(
    (status) => {
      setDraftStatus(status);
      setDraftSub(status === loan?.loanStatus ? loan?.subStatus : null);
    },
    [loan?.loanStatus, loan?.subStatus],
  );

  const handleTerminalClick = useCallback(
    (terminal) => {
      if (terminal.key === loan?.loanStatus) return;
      if (terminal.opensModal) {
        // The modal owns the write from here, so drop any draft the user had
        // going — otherwise cancelling it and reopening the panel would show a
        // stale selection.
        closePanel();
        setModal(terminal.key);
        return;
      }
      handleSelectStatus(terminal.key);
    },
    [loan?.loanStatus, closePanel, handleSelectStatus],
  );

  const handleSave = () => {
    updateLoanStatusOrSubStatus({
      loanId: loan?._id,
      loanStatus: draftStatus,
      subStatus: draftSub,
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        dispatch(loanPipelineSet(null));
        setOpen(false);
      },
    });
  };

  return (
    <Flx
      ac={!compact}
      column={compact}
      g={compact ? 0.5 : 1}
      sx={{ flexShrink: 0 }}
    >
      <Txt
        sx={{
          fontSize: compact ? "11px" : 10.5,
          textTransform: compact ? "none" : "uppercase",
          letterSpacing: compact ? 0 : "0.08em",
          color: compact ? "#64748b" : T.ink3,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {compact ? "Status" : "Loan status"}
      </Txt>
      <Box sx={{ position: "relative", mt: compact ? "-5px" : "0px" }}>
        <Box
          component="button"
          ref={anchorRef}
          onClick={() => (open ? closePanel() : setOpen(true))}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: compact ? 0.75 : 1,
            background: tone.bg,
            color: tone.fg,
            border: `1px solid ${tone.line}`,
            px: compact ? "3px" : "12px",
            py: compact ? "3px" : "8px",
            pl: compact ? "10px" : "14px",

            // px: compact ? "8px" : "12px",
            // py: compact ? "3px" : "8px",
            // pl: compact ? "10px" : "14px",
            borderRadius: compact ? "6px" : "8px",
            fontSize: compact ? 11 : 13,
            fontWeight: compact ? 600 : 500,
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            transition: "filter 0.15s",
            "&:hover": { filter: "brightness(0.97)" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: compact ? 7 : 8,
              height: compact ? 7 : 8,
            }}
          >
            <Box
              sx={{
                width: compact ? 7 : 8,
                height: compact ? 7 : 8,
                borderRadius: "50%",
                background: tone.fg,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: "-3px",
                borderRadius: "50%",
                border: `2px solid ${tone.fg}`,
                opacity: 0.3,
                animation: "altpulse 2s ease-out infinite",
                "@keyframes altpulse": {
                  "0%": { transform: "scale(0.8)", opacity: 0.4 },
                  "100%": { transform: "scale(1.6)", opacity: 0 },
                },
              }}
            />
          </Box>
          {/* Status + sub-status when present, e.g. "Processing → Pre-Underwrite".
              Chevron separator signals the parent → sub-status relationship;
              sub-status is rendered with reduced opacity so the parent stays
              the visual anchor. */}
          <Box component="span">{loan?.loanStatus || "Unknown"}</Box>
          {loan?.subStatus && (
            <>
              <ChevronRightOutlined
                sx={{ fontSize: 16, opacity: 0.55, mx: "-4px" }}
              />
              <Box component="span" sx={{ fontWeight: 400, opacity: 0.85 }}>
                {/* An on-hold acquisition's subStatus is the SF stage it
                    was held at. */}
                {isAcq && loan?.loanStatus === ACQUISITION_ON_HOLD_STATUS
                  ? `held at ${loan.subStatus}`
                  : loan.subStatus}
              </Box>
            </>
          )}
          <ExpandMoreRounded sx={{ fontSize: 16 }} />
        </Box>

        {/* Portal-based Popover so it never grows / gets clipped by an ancestor
            scroll container (the old absolute panel overflowed the header). */}
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={closePanel}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                background: T.surface,
                border: `1px solid ${T.line}`,
                borderRadius: 2,
                boxShadow: SHADOW_MD,
                overflow: "hidden",
              },
            },
          }}
        >
          <Flx sx={{ alignItems: "stretch" }}>
            <Box sx={{ p: 1, minWidth: 208 }}>
              <ColumnLabel>Status</ColumnLabel>
              {heldStatus && (
                <>
                  <StatusOptionRow
                    label={`Resume at ${heldStatus}`}
                    icon={PlayCircleOutlineRounded}
                    color={STATUS_TONE[heldStatus]?.fg}
                    selected={draftStatus === heldStatus}
                    onClick={() => handleSelectStatus(heldStatus)}
                  />
                  <Box
                    sx={{ borderTop: `1px solid ${T.line}`, my: 0.75, mx: 0.5 }}
                  />
                </>
              )}
              {stageOptions.map((status) => (
                <StatusOptionRow
                  key={status}
                  label={status}
                  selected={draftStatus === status}
                  isCurrent={loan?.loanStatus === status}
                  onClick={() => handleSelectStatus(status)}
                />
              ))}

              <Box
                sx={{ borderTop: `1px solid ${T.line}`, my: 0.75, mx: 0.5 }}
              />

              {terminalStatuses.map((terminal) => (
                <StatusOptionRow
                  key={terminal.key}
                  label={terminal.label}
                  icon={terminal.icon}
                  color={STATUS_TONE[terminal.key]?.fg}
                  selected={draftStatus === terminal.key}
                  isCurrent={loan?.loanStatus === terminal.key}
                  onClick={() => handleTerminalClick(terminal)}
                />
              ))}
            </Box>

            {/* Sub-status list stacks to the right of the status list, and
                    only shows for statuses that offer one. Picking a sub-status
                    is optional — a "None" row lets the user clear it. */}
            {hasSubOptions && (
              <Box
                sx={{
                  p: 1,
                  minWidth: 208,
                  borderLeft: `1px solid ${T.line}`,
                  background: T.surface3,
                }}
              >
                <ColumnLabel>Sub-status (optional)</ColumnLabel>
                <StatusOptionRow
                  label="None"
                  selected={isNil(draftSub)}
                  isCurrent={isNil(loan?.subStatus)}
                  onClick={() => setDraftSub(null)}
                />
                {subOptions.map((sub) => (
                  <StatusOptionRow
                    key={sub}
                    label={sub}
                    selected={draftSub === sub}
                    isCurrent={loan?.subStatus === sub}
                    onClick={() => setDraftSub(sub)}
                  />
                ))}
              </Box>
            )}
          </Flx>

          <Flx ac end g={1} sx={{ borderTop: `1px solid ${T.line}`, p: 1 }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={closePanel}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              loading={loading}
              disabled={!canSave}
              onClick={handleSave}
            >
              Confirm
            </Button>
          </Flx>
        </Popover>
      </Box>

      <Suspense fallback={null}>
        {modal === "Closed" && (
          <MarkLoanClosedModal onClose={() => setModal(null)} />
        )}
        {modal === "Dead" && (
          <MarkLoanDeadModal onClose={() => setModal(null)} />
        )}
      </Suspense>
    </Flx>
  );
};

const ColumnLabel = ({ children }) => (
  <Txt
    sx={{
      display: "block",
      fontSize: 10.5,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: T.ink4,
      fontWeight: 500,
      px: 1,
      pt: 0.5,
      pb: 1,
    }}
  >
    {children}
  </Txt>
);

const StatusOptionRow = ({
  label,
  selected,
  isCurrent,
  onClick,
  icon: Icon,
  color,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      width: "100%",
      textAlign: "left",
      px: 1,
      py: 0.85,
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 14,
      lineHeight: 1.3,
      fontWeight: selected ? 600 : 400,
      color: color || T.ink,
      background: selected ? "#dbeafe" : "transparent",
      "&:hover": { background: selected ? "#dbeafe" : T.surface2 },
    }}
  >
    {Icon && <Icon sx={{ fontSize: 17, flexShrink: 0 }} />}
    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
      {label}
    </Box>
    {isCurrent && (
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.5,
          flexShrink: 0,
        }}
      />
    )}
  </Box>
);

/* ─── Stage tracker ─────────────────────────────────────────
   Thin bars instead of chevrons. Done = filled, current =
   half-filled, future = empty. Each stage's date comes from
   milestoneStatusChanges — same source the Key Dates & Docs
   tab uses — so the row reads as a real mini-timeline.
   Fallbacks: createDateTime → "Lead" when no transition was
   logged, expectedClosingDate → future "Clear To Close" target.
*/

// The acquisition sub-stage steps row under the tracker is hidden until the
// step definitions are final ("these are not 100% yet", 2026-10-14). Flip to
// true to bring the row back; nothing else changes.
const SHOW_ACQUISITION_STAGE_STEPS = false;

export const StageTracker = ({ loan }) => {
  const isAcq = useIsAcquisitions();
  const stages = isAcq ? ACQUISITION_STAGES : STAGES;
  // Acquisitions has no sub-statuses, so the sub→parent rollup is a no-op ({}).
  const subToParent = isAcq ? EMPTY_MAP : SUB_TO_PARENT;

  // An on-hold acquisition is drawn at the stage it was held at.
  const { key: effectiveCurrent, onHold } = resolveStagePosition(loan, isAcq);
  const currentIdx = stages.findIndex((s) => s.key === effectiveCurrent);
  const currentSteps = isAcq && SHOW_ACQUISITION_STAGE_STEPS
    ? acquisitionStageSteps(loan, effectiveCurrent)
    : [];
  // Settlement target per loan-acquisitions.md: the firm settlementDate, else
  // the soft expectedSettlementDate.
  const settlementTarget = loan?.settlementDate || loan?.expectedSettlementDate;

  // Build a map: stage.key → earliest ms the loan was moved INTO that stage.
  // Sub-statuses (e.g. "Pre-Underwrite", "Docs Out") are normalized to their
  // parent stage via SUB_TO_PARENT so a row that landed on a sub-status
  // still credits the correct top-level slot.
  const stageEnteredMs = useMemo(() => {
    const map = {};
    const changes = loan?.milestoneStatusChanges || [];
    for (const c of changes) {
      const updated = c?.updatedMilestoneStatus;
      if (!updated) continue;
      const parent = subToParent[updated] || updated;
      const raw = c?.milestoneChangeDate;
      const t = raw ? timestampMs(raw) : null;
      if (t == null || Number.isNaN(t)) continue;
      if (map[parent] == null || t < map[parent]) {
        map[parent] = t;
      }
    }
    return map;
  }, [loan?.milestoneStatusChanges, subToParent]);

  // Build a map: stage.key → total ms the loan has spent in that stage.
  // Each change marks entry INTO its updatedMilestoneStatus, which lasts until
  // the next chronological change (or "now" for the most recent one). The
  // status *before* the first logged change can't be bounded (we don't know
  // when it was entered), so it's intentionally left out — this is the
  // "if it's able to be determined" part of the ask. Sub-statuses roll up to
  // their parent stage via SUB_TO_PARENT so totals line up with the rows.
  const stageDurationMs = useMemo(() => {
    const changes = (loan?.milestoneStatusChanges || [])
      .map((c) => ({
        updated: c?.updatedMilestoneStatus,
        t: c?.milestoneChangeDate
          ? timestampMs(c.milestoneChangeDate)
          : null,
      }))
      .filter((c) => c.t != null && !Number.isNaN(c.t))
      .sort((a, b) => a.t - b.t);
    if (!changes.length) return {};

    const now = Date.now();
    const totals = {};
    for (let i = 0; i < changes.length; i++) {
      const start = changes[i].t;
      const end = i + 1 < changes.length ? changes[i + 1].t : now;
      const ms = end - start;
      const parent = subToParent[changes[i].updated] || changes[i].updated;
      if (!parent || ms <= 0) continue;
      totals[parent] = (totals[parent] || 0) + ms;
    }
    return totals;
  }, [loan?.milestoneStatusChanges, subToParent]);

  const stageRows = stages.map((s, idx) => {
    let state = "future";
    if (currentIdx >= 0) {
      if (idx < currentIdx) state = "done";
      else if (idx === currentIdx) state = "current";
    }
    let dateText = "—";
    const enteredMs = stageEnteredMs[s.key];
    if (enteredMs != null) {
      dateText = longMonthDay(enteredMs);
    } else if (idx === 0 && loan?.createDateTime) {
      // No "into Lead" transition was logged — Lead is typically the
      // initial state, so fall back to when the loan was created.
      dateText = longMonthDay(timestampMs(loan.createDateTime));
    } else if (
      !isAcq &&
      s.key === "Clear To Close" &&
      state === "future" &&
      loan?.expectedClosingDate
    ) {
      dateText = `${formatDate(loan.expectedClosingDate).split(",")[0]} (target)`;
    } else if (
      isAcq &&
      s.key === "Settled" &&
      state === "future" &&
      settlementTarget
    ) {
      dateText = `${formatDate(settlementTarget).split(",")[0]} (target)`;
    }

    // "(N days)" next to the label when the time spent in this stage can be
    // determined from milestoneStatusChanges. Sub-day stints show "<1d".
    let durationText = null;
    const durMs = stageDurationMs[s.key];
    if (durMs != null && durMs > 0) {
      const days = durMs / 86_400_000;
      durationText = days < 1 ? "<1d" : `${Math.round(days)}d`;
    }
    return {
      ...s,
      state,
      dateText,
      durationText,
      onHold: onHold && state === "current",
    };
  });

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
            lg: `repeat(${stages.length}, 1fr)`,
          },
          gap: "6px",
        }}
      >
        {stageRows.map((s) => (
          <StageCell key={s.key} {...s} />
        ))}
      </Box>
      {!isEmpty(currentSteps) && (
        <StageStepsRow milestone={effectiveCurrent} steps={currentSteps} />
      )}
    </Box>
  );
};

/* The current acquisition milestone's tracked steps (stageTracking), as a
   single wrapping row of done / pending checks under the tracker. Read-only:
   each step is a loan field (the Due Diligence dates are stamped by the
   acquisition lifecycle), so it ticks as those fields are filled. */
const stepValueText = (v) => {
  if (isNil(v) || v === "" || v === false) return null;
  if (v === true) return null;
  const s = String(v);
  // Dates arrive as ISO strings; anything else (lpsStatus, tradeId) as-is.
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? formatDate(s).split(",")[0] : s;
};

const StageStepsRow = ({ milestone, steps }) => {
  const done = steps.filter((s) => s.done).length;
  return (
    <Flx ac wrap g={1.5} sx={{ mt: 2 }}>
      <Txt
        sx={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: T.ink3,
          fontWeight: 500,
        }}
      >
        {milestone} steps · {done}/{steps.length}
      </Txt>
      {steps.map((s) => {
        const value = stepValueText(s.value);
        const Icon = s.done
          ? CheckCircleOutlineRounded
          : RadioButtonUncheckedRounded;
        return (
          <Flx key={s.field} ac g={0.5}>
            <Icon sx={{ fontSize: 15, color: s.done ? T.good : T.ink4 }} />
            <Txt sx={{ fontSize: 12.5, color: s.done ? T.ink2 : T.ink3 }}>
              {s.step}
              {value && (
                <Txt
                  component="span"
                  sx={{
                    fontSize: 11.5,
                    color: T.ink3,
                    fontFamily: FONT_MONO,
                    ml: "6px",
                  }}
                >
                  {value}
                </Txt>
              )}
            </Txt>
          </Flx>
        );
      })}
    </Flx>
  );
};

const StageCell = ({ label, state, dateText, durationText, onHold }) => {
  // On hold: the held-at stage keeps its half-filled look, in the warning tone.
  const accent = onHold ? T.warn : T.info;
  const barBg =
    state === "done"
      ? T.info
      : state === "current"
        ? `linear-gradient(to right, ${accent} 50%, ${T.line2} 50%)`
        : T.line2;
  const nameColor =
    state === "current" ? accent : state === "done" ? T.ink2 : T.ink3;
  const dateColor =
    state === "current" ? accent : state === "done" ? T.ink3 : T.ink4;
  return (
    <Box>
      <Box
        sx={{
          height: 4,
          background: barBg,
          borderRadius: "2px",
          mb: "10px",
        }}
      />
      <Txt
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: state === "current" ? 600 : 500,
          color: nameColor,
          mb: "2px",
        }}
      >
        {label}
        {onHold && " · on hold"}
        {durationText && (
          <Txt
            component="span"
            sx={{
              fontSize: 10,
              textTransform: "none",
              letterSpacing: 0,
              fontWeight: 500,
              color: T.ink4,
              ml: "4px",
            }}
          >
            ({durationText})
          </Txt>
        )}
      </Txt>
      <Txt
        sx={{
          fontSize: 10.5,
          color: dateColor,
          fontFamily: FONT_MONO,
        }}
      >
        {dateText}
      </Txt>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════
   ZONE 2 — KPI ROW
   Five figures only. Total Loan Amount is highlighted (the
   headline). Construction-only / refi-only metrics are kept
   off this row even when zero — the row shouldn't grow.
   ═══════════════════════════════════════════════════════════ */

export const KpiRow = ({ loan }) => {
  const arvDelta = useMemo(() => {
    const arv = Number(loan?.totalArv);
    const asIs = Number(loan?.totalAsIsValue);
    if (!Number.isFinite(arv) || !Number.isFinite(asIs) || asIs <= 0) {
      return null;
    }
    const pct = ((arv - asIs) / asIs) * 100;
    if (!Number.isFinite(pct)) return null;
    const sign = pct > 0 ? "+" : "";
    return { label: `${sign}${pct.toFixed(0)}% as-is`, positive: pct > 0 };
  }, [loan?.totalArv, loan?.totalAsIsValue]);

  const baseLtvDelta = useMemo(() => {
    if (isNil(loan?.baseLoanToAsIsValue)) return null;
    return `Base · LTV ${formatPercent(loan?.baseLoanToAsIsValue)}`;
  }, [loan?.baseLoanToAsIsValue]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(5, 1fr)",
        },
        gap: 2,
        mb: 3,
      }}
    >
      <KpiCard
        highlight
        label="Total loan amount"
        value={formatDollar(loan?.totalLoanAmount, "—")}
        delta={baseLtvDelta}
      />
      <KpiCard
        label="Total purchase price"
        value={formatDollar(loan?.totalPurchasePrice, "—")}
      />
      <KpiCard
        label="Total ARV"
        value={formatDollar(loan?.totalArv, "—")}
        delta={arvDelta?.label}
        deltaColor={arvDelta?.positive ? T.good : T.ink3}
      />
      <KpiCard
        label="Total LTARV"
        value={
          isNil(loan?.totalLoanToArv)
            ? "—"
            : formatPercent(loan?.totalLoanToArv)
        }
      />
      <KpiCard
        label="Interest rate"
        value={
          isNil(loan?.interestRate) ? "—" : formatPercent(loan?.interestRate)
        }
        delta={loan?.interestCalcType}
      />
    </Box>
  );
};

const KpiCard = ({ label, value, delta, deltaColor, highlight = false }) => (
  <Box
    sx={{
      background: highlight
        ? `linear-gradient(180deg, ${T.accentSoft} 0%, ${T.surface} 60%)`
        : T.surface,
      border: `1px solid ${highlight ? T.accentLine : T.line}`,
      borderRadius: "12px",
      boxShadow: SHADOW_SM,
      p: "16px 18px",
    }}
  >
    <Txt
      sx={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: T.ink3,
        fontWeight: 500,
        mb: "6px",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontFamily: FONT_MONO,
        fontSize: 20,
        fontWeight: 500,
        color: highlight ? T.accent : T.ink,
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
      }}
    >
      {value ?? "—"}
    </Txt>
    {delta && (
      <Txt
        sx={{
          fontSize: 11,
          color: deltaColor || T.ink3,
          mt: "4px",
          fontFamily: FONT_MONO,
        }}
      >
        {delta}
      </Txt>
    )}
  </Box>
);

/* ═══════════════════════════════════════════════════════════
   ZONE 3 — LOAN INFO + ASSIGNED TEAM
   Two-column row, info gets the wider lane. Info renders as
   key/value rows (text by default, hover ✎ on editable);
   computed values (LTV, points) carry no edit affordance.
   Assignees show empty roles as dashed-border placeholder
   avatars rather than hiding them.
   ═══════════════════════════════════════════════════════════ */

export const MainGrid = ({ loan, baseUrl, navigate }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
        gap: 3,
        mb: 3,
      }}
    >
      <LoanInfoCard loan={loan} baseUrl={baseUrl} navigate={navigate} />
      <Flx column g={3} sx={{ minWidth: 0 }}>
        <AssignedTeamCard loan={loan} />
        <BoxFoldersCard loan={loan} />
      </Flx>
    </Box>
  );
};

/* ─── Box document folders ──────────────────────────────────
   Quick links out to the Box folders where the loan's and the
   correspondent's documents live. Read-only; opens in a new tab.
*/
const BoxFoldersCard = ({ loan }) => {
  const folders = [
    {
      label: "Loan folder",
      url: loan?.box_loan_folder_url,
    },
    {
      label: "Correspondent folder",
      url: loan?.box_correspondent_folder_url,
    },
  ];
  return (
    <Card sx={{ overflow: "unset" }}>
      <CardHead
        title="Document folders"
        icon={<FolderOpenOutlined className="thin" />}
      />
      <Box>
        {folders.map((f, i) => (
          <BoxFolderRow
            key={f.label}
            label={f.label}
            url={f.url}
            isLast={i === folders.length - 1}
          />
        ))}
      </Box>
    </Card>
  );
};

const BoxFolderRow = ({ label, url, isLast }) => {
  const linked = Boolean(url);
  return (
    <Box
      component={linked ? "a" : "div"}
      href={linked ? url : undefined}
      target={linked ? "_blank" : undefined}
      rel={linked ? "noopener noreferrer" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: "16px",
        py: "12px",
        textDecoration: "none",
        borderBottom: isLast ? "none" : `1px dashed ${T.line}`,
        cursor: linked ? "pointer" : "default",
        transition: "background 0.15s",
        "&:hover": linked ? { background: T.surface3 } : undefined,
      }}
    >
      <Flx ac g={1.25} sx={{ minWidth: 0 }}>
        <Flx
          center
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            flexShrink: 0,
            background: linked ? T.accentSoft : T.surface2,
            border: `1px solid ${linked ? T.accentLine : T.line2}`,
            color: linked ? T.accent : T.ink4,
          }}
        >
          <FolderOpenOutlined sx={{ fontSize: 15 }} />
        </Flx>
        <Flx column sx={{ minWidth: 0 }}>
          <Txt
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Txt>
          <Txt
            sx={{
              fontSize: 11.5,
              color: linked ? T.ink3 : T.ink4,
              fontStyle: linked ? "normal" : "italic",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {linked ? url : "Not linked"}
          </Txt>
        </Flx>
      </Flx>
      {linked && (
        <Flx center sx={{ color: T.ink3, flexShrink: 0 }}>
          <OpenInNewRounded sx={{ fontSize: 15 }} />
        </Flx>
      )}
    </Box>
  );
};

const LoanInfoCard = ({ loan, baseUrl, navigate }) => {
  const rows = useMemo(
    () => [
      {
        label: "Pipeline",
        value: loan?.pipeline,
        editable: true,
      },
      {
        label: "Total holdback",
        value: formatDollar(loan?.totalHoldback, "$0"),
        editable: true,
      },
      {
        label: "Loan product",
        value: loan?.loanProductType,
        editable: true,
      },
      {
        label: "Interest calc",
        value: loan?.interestCalcType,
        editable: true,
      },
      {
        label: "Loan purpose",
        value: loan?.loanPurpose,
        editable: true,
      },
      {
        label: "Discount points",
        value: !isNil(loan?.discountPoints)
          ? formatPercent(loan?.discountPoints)
          : "—",
        computed: true,
        computedTooltip:
          "Auto-calculated from loan pricing — % of base loan amount paid up-front.",
      },
      {
        label: "Expected closing",
        value: loan?.expectedClosingDate
          ? formatDate(loan.expectedClosingDate)
          : null,
        editable: true,
      },
      {
        label: "Origination points",
        value: !isNil(loan?.originationPoints)
          ? formatPercent(loan?.originationPoints)
          : "—",
        computed: true,
        computedTooltip:
          "Auto-calculated from loan pricing — % of base loan amount the originator earns.",
      },
      {
        label: "Base loan to as-is",
        value: !isNil(loan?.baseLoanToAsIsValue)
          ? formatPercent(loan?.baseLoanToAsIsValue)
          : "—",
        computed: true,
        computedTooltip:
          "Base Loan Amount ÷ Σ As-Is Value across every subject property.",
      },
      {
        label: "Base loan to cost",
        value: !isNil(loan?.baseLoanToCost)
          ? formatPercent(loan?.baseLoanToCost)
          : "—",
        computed: true,
        computedTooltip:
          "Base Loan Amount ÷ Σ Cost Basis. Per-property Cost Basis = Purchase Price + Assignment Fees + Additional Basis.",
      },
      {
        label: "Repeat applicant",
        value: size(loan?.borrowers?.filter((b) => b?.repeatBorrowerFlag))
          ? "Yes"
          : "No",
        computed: true,
        computedTooltip:
          "Yes when at least one applicant on this loan has the repeat-borrower flag set.",
      },
      {
        label: "Base loan to purchase",
        value: !isNil(loan?.baseLoanToPurchasePrice)
          ? formatPercent(loan?.baseLoanToPurchasePrice)
          : "—",
        computed: true,
        computedTooltip:
          "Base Loan Amount ÷ Σ Purchase Price across every subject property.",
      },
    ],
    [loan],
  );

  return (
    <Card>
      <CardHead
        title="Loan information"
        icon={<InfoOutlined className="thin" />}
        endContent={
          <CardLink onClick={() => navigate(`${baseUrl}/loan-information`)}>
            Edit details →
          </CardLink>
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          py: "4px",
        }}
      >
        {rows.map((row, idx) => (
          <InfoRow
            key={row.label}
            row={row}
            // strip the bottom border on the final two rows of each column
            // so the table doesn't double-line at the card footer
            isLast={idx >= rows.length - 2}
          />
        ))}
      </Box>
    </Card>
  );
};

const InfoRow = ({ row, isLast }) => (
  <Flx
    ac
    jb
    g={1.5}
    sx={{
      px: "20px",
      py: "10px",
      borderBottom: isLast ? "none" : `1px solid ${T.line2}`,
    }}
  >
    <Txt
      sx={{
        fontSize: 12,
        color: T.ink3,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 500,
      }}
    >
      {row.label}
    </Txt>
    {/* Every value uses the same JetBrains Mono treatment so the column
        reads consistently. Editability is signaled by a persistent very
        light blue tint behind the value. Computed (non-editable derived)
        values get a small Σ icon with a tooltip describing the formula —
        same affordance as the existing redesign's DetailRow. */}
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 0.6,
        textAlign: "right",
        fontWeight: 500,
        fontSize: 13,
        color: "#0f172a",
        fontFamily: FONT_MONO,
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
        ...(row.editable
          ? {
              cursor: "pointer",
              px: 1,
              py: "5px",
              borderRadius: "4px",
              background: "#eaf2ff",
              border: "1px solid #d6e6ff",
              transition: "background 0.15s, border-color 0.15s",
              "&:hover": {
                background: "#dceaff",
                borderColor: "#bcd5ff",
              },
            }
          : {}),
      }}
    >
      {row.computed && (
        <Tooltip
          title={row.computedTooltip || "This is an auto-calculated value"}
          arrow
          placement="left"
        >
          <Functions
            sx={{
              fontSize: 13,
              color: T.good,
              cursor: "help",
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
      <span>{row.value || "—"}</span>
    </Box>
  </Flx>
);

/* ─── Assigned team ────────────────────────────────────────
   Empty roles render as dashed-border placeholders so missing
   staffing is visible at a glance. Last-edited-by is treated
   as another assignee row at the bottom — same chrome.
*/

const ROLE_PALETTE = [
  T.accent,
  T.info,
  T.warn,
  T.good,
  "#5e3aa8",
  T.ink2, // 5 — Junior Underwriter (Pre-UW Team)
  T.urgent, // 6 — QC Review (QC Team)
];

// Each role row needs its own field name on the loan (the key in
// `updateLoanData({ data: { [field]: email } })`). "Last edited by"
// has no field — it's read-only, so it gets `field: null`.
export const AssignedTeamCard = ({ loan }) => {
  return (
    <Card sx={{ overflow: "unset" }}>
      <CardHead
        title="Assigned team"
        icon={<GroupsOutlined className="thin" />}
        endContent={
          <Tooltip title="Manage assignments">
            <IconButton
              size="small"
              sx={{
                width: 26,
                height: 26,
                border: `1px solid ${T.line}`,
                borderRadius: "5px",
                color: T.ink3,
              }}
            >
              <SettingsOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        }
      />
      <AssignedTeamRows loan={loan} />
    </Card>
  );
};

// Just the assignee rows (no card chrome) — reusable inside other card shells.
export const AssignedTeamRows = ({ loan }) => {
  const users = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { updateLoanData, loading } = useUnderwritingHook();
  // Which row's popover is open. `null` = none.
  const [editingField, setEditingField] = useState(null);

  // Lendmarq divergence (see CLAUDE.md #1): role set is Loan Officer / Sales
  // Analyst / Processor / Underwriter / Closer, keyed to the canonical loan
  // fields (matches rtl-portal-frontend). No per-role roster and no
  // appendAdminSelf — every row's picker lists the full fetched users list
  // (AssigneeRow → AssignPopover falls back to SelectUserEmailInput when no
  // `options` are passed). Do NOT reintroduce Eastview's Account Manager /
  // Structurer / Junior Underwriter (preUnderwriter) / QC Review (qc_analyst)
  // roles or roster-scoped `options` here during a sync — those are EV-only.
  const roles = useMemo(
    () => [
      {
        role: "Loan Officer",
        field: "salesperson",
        email: loan?.salesperson,
        colorIdx: 0,
      },
      {
        role: "Sales Analyst",
        field: "salesAnalyst",
        email: loan?.salesAnalyst,
        colorIdx: 4,
      },
      {
        role: "Processor",
        field: "processor",
        email: loan?.processor,
        colorIdx: 2,
      },
      {
        role: "Underwriter",
        field: "underwriter",
        email: loan?.underwriter,
        colorIdx: 1,
      },
      {
        role: "Closer",
        field: "loanCloser",
        email: loan?.loanCloser,
        colorIdx: 3,
      },
    ],
    [
      loan?.salesperson,
      loan?.salesAnalyst,
      loan?.processor,
      loan?.underwriter,
      loan?.loanCloser,
    ],
  );

  const handleSave = useCallback(
    (field, newEmail) => {
      if (!loan?._id) return;
      updateLoanData({
        loanId: loan._id,
        data: { [field]: newEmail || null },
        onSuccessFn: (res) => {
          dispatch(loanDrilldownSet(res));
          setEditingField(null);
        },
      });
    },
    [loan?._id, updateLoanData, dispatch],
  );

  return (
    <Box>
      {roles.map((r, idx) => (
        <AssigneeRow
          key={r.role}
          field={r.field}
          role={r.role}
          email={r.email}
          users={users}
          // Lendmarq divergence: every role is editable and picks from the full
          // fetched users list (no rosters). AssignPopover renders
          // SelectUserEmailInput whenever `options` is absent.
          readOnly={false}
          color={ROLE_PALETTE[r.colorIdx]}
          isOpen={editingField === r.field}
          saving={loading}
          onClick={() => setEditingField(r.field)}
          onClose={() => setEditingField(null)}
          isLast={idx === roles.length - 1}
          onSave={(newEmail) => handleSave(r.field, newEmail)}
        />
      ))}
    </Box>
  );
};

const AssigneeRow = ({
  field,
  role,
  email,
  users,
  options,
  color,
  subtitleOverride,
  isLast,
  readOnly,
  isOpen,
  saving,
  onClick,
  onClose,
  onSave,
}) => {
  // Prefer the roster label (full name) for the assigned email, then fall back
  // to the global users list, then the raw email.
  const rosterLabel = options?.find((o) => o?.value === email)?.label;
  const fullName = email
    ? rosterLabel || getUserFullNameFromEmail(users, email)
    : null;
  const initials = fullName
    ? fullName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase()
    : null;

  const empty = !email;
  const clickable = !readOnly;
  const rowRef = useRef(null);

  return (
    // `data-drilldown-field` = `?focus=` deep-link target (useDrilldownFieldFocus).
    <Box sx={{ position: "relative" }} ref={rowRef} data-drilldown-field={field || undefined}>
      <FlxGrid
        ac
        fw
        jb
        g={1.5}
        onClick={clickable ? onClick : undefined}
        sx={{
          display: "grid",
          // Two columns: role label on the left, assignee content on the right.
          // gridTemplateColumns: "1fr 2fr",
          gridTemplateColumns: `140px 1fr`,
          alignItems: "center",

          px: "8px",
          py: "6px",
          // px: "20px",
          // py: "12px",

          borderBottom: isLast ? "none" : `1px dashed #dde0e4`,
          // borderBottom: isLast ? "none" : `1px solid ${T.line2}`,
          cursor: clickable ? "pointer" : "default",
          transition: "background 0.15s",
          background: isOpen ? T.surface3 : "transparent",
          "&:hover": clickable
            ? {
                background: T.surface3,
                borderBottomLeftRadius: isLast ? 10 : 0,
                borderBottomRightRadius: isLast ? 10 : 0,
              }
            : undefined,
        }}
      >
        <Flx ac g={1}>
          <Txt
            sx={{
              fontSize: 12,
              // textTransform: "uppercase",
              // letterSpacing: "0.06em",
              color: T.ink3,
              fontWeight: 500,
              // width: 90,
              flexShrink: 0,
            }}
          >
            {role}
          </Txt>
        </Flx>
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          {!empty && (
            <Avatar
              sx={{
                width: 22,
                height: 22,
                // width: 30,
                // height: 30,
                fontSize: 9,
                fontWeight: 600,
                textTransform: "uppercase",
                background: empty ? T.surface2 : color,
                color: empty ? T.ink4 : "#fff",
                border: empty ? `1px dashed ${T.ink4}` : "none",
              }}
            >
              {empty ? "+" : initials || "?"}
            </Avatar>
          )}
          <Flx g={1} ac sx={{ flex: 1, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 12,
                // fontSize: 13.5,
                color: empty ? T.ink3 : T.ink,
                fontWeight: empty ? 400 : 500,
                fontStyle: empty ? "italic" : "normal",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {empty ? "Not assigned" : fullName || email}
            </Txt>
            {/* Subtitle stays in body sans for visual consistency with the
              names — mono looked out of place on this row. */}
            <Txt
              sx={{
                fontSize: 11.5,
                color: T.ink3,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {empty
                ? null
                : subtitleOverride ||
                  // Only show the <email> subtitle when the primary line shows a
                  // real name. If there's no full name (or it equals the email),
                  // the primary line already IS the email — don't repeat it.
                  (fullName && fullName !== email ? `<${email}>` : null)}
            </Txt>
          </Flx>
          {/* Explicit edit affordance when someone is assigned, so it's clear
              the row is clickable to change the person. Read-only rows keep the
              button as a hidden placeholder so every row stays the same height. */}
          <Tooltip title={clickable ? `Change ${role}` : ""}>
            <IconButton
              size="small"
              aria-hidden={!clickable}
              tabIndex={clickable ? 0 : -1}
              onClick={
                clickable
                  ? (e) => {
                      e.stopPropagation();
                      onClick?.();
                    }
                  : undefined
              }
              sx={{
                flexShrink: 0,
                color: T.ink3,
                // border: `1px solid ${T.line}`,
                borderRadius: "5px",
                width: 26,
                height: 26,
                visibility: clickable ? "visible" : "hidden",
                pointerEvents: clickable ? undefined : "none",
                "&:hover": { color: T.ink, background: T.surface3 },
              }}
            >
              <EditOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          {/* {!empty && clickable && (
            <Tooltip title={`Change ${role}`}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.();
                }}
                sx={{
                  flexShrink: 0,
                  color: T.ink3,
                  border: `1px solid ${T.line}`,
                  borderRadius: "5px",
                  width: 26,
                  height: 26,
                  "&:hover": { color: T.ink, background: T.surface3 },
                }}
              >
                <EditOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )} */}
        </Flx>
      </FlxGrid>
      <AssignPopover
        open={isOpen && !readOnly}
        anchorEl={rowRef.current}
        currentEmail={email}
        options={options}
        saving={saving}
        onClose={onClose}
        onSave={onSave}
        role={role}
      />
    </Box>
  );
};

// Popover for assigning / re-assigning a role. Rendered in a portal (MUI
// Popover) anchored to the row, so it's never clipped by the card's overflow.
// Uses SelectUserEmailInput to stay consistent with the rest of the app's
// user-selector dropdowns. "Clear" sends null to remove the assignment.
const AssignPopover = ({
  open,
  anchorEl,
  currentEmail,
  options,
  role,
  saving,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState(currentEmail || null);
  // Reset the draft to the current value each time the popover opens.
  useEffect(() => {
    if (open) setDraft(currentEmail || null);
  }, [open, currentEmail]);

  // When a role ships a hardcoded roster, render those as `{label} <{value}>`
  // items. Always keep the currently-assigned email selectable (even if it's
  // not in the roster) so the dropdown prepopulates instead of going blank.
  const hasRoster = Array.isArray(options) && options.length > 0;
  const roleOptions = useMemo(() => {
    if (!hasRoster) return [];
    const mapped = options.map((o) => ({
      label: `${o.label} <${o.value}>`,
      value: o.value,
    }));
    if (currentEmail && !mapped.some((o) => o.value === currentEmail)) {
      mapped.push({ label: currentEmail, value: currentEmail });
    }
    return mapped;
  }, [hasRoster, options, currentEmail]);

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      // Don't lock body scroll / add scrollbar-compensation padding — that
      // shifts the card width while the popover is open (made rows beneath it,
      // like the last one, look like a different width).
      disableScrollLock
      anchorOrigin={{ vertical: "center", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            maxWidth: "356px",
            width: anchorEl?.offsetWidth ? anchorEl.offsetWidth - 24 : 320,
            background: T.surface,
            border: `1px solid ${T.line}`,
            borderRadius: 2,
            boxShadow: SHADOW_MD,
            p: 1.5,
            overflow: "visible",
            ".MuiSelect-select, .MuiInputBase-input": { fontSize: 13 },
          },
        },
      }}
    >
      <Flx column g={1}>
        <Txt
          sx={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: T.ink3,
            fontWeight: 500,
          }}
        >
          Assign {role}
        </Txt>
        {hasRoster ? (
          <SelectInput
            value={draft}
            onChange={(v) => setDraft(v)}
            options={roleOptions}
            placeholder={`Select ${role}`}
          />
        ) : (
          <SelectUserEmailInput value={draft} onChange={setDraft} />
        )}
        <Flx ac jb sx={{ mt: 0.5 }}>
          <Button
            size="small"
            color="error"
            variant="text"
            disabled={saving || !currentEmail}
            onClick={() => onSave(null)}
          >
            Clear
          </Button>
          <Flx ac g={0.5}>
            <Button size="small" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              loading={saving}
              disabled={draft === currentEmail}
              onClick={() => onSave(draft)}
              sx={{
                background: T.accent,
                "&:hover": { background: "#234d3a" },
              }}
            >
              Save
            </Button>
          </Flx>
        </Flx>
      </Flx>
    </Popover>
  );
};

/* ═══════════════════════════════════════════════════════════
   ZONE 4 — RELATED ENTITIES
   Three equal columns (Borrowers / Properties / Contacts).
   Same row structure across all three so the user learns the
   pattern once. Inline "Add" row at the bottom of each list.
   ═══════════════════════════════════════════════════════════ */

export const EntitiesGrid = ({ loan, baseUrl, navigate }) => {
  const dispatch = useDispatch();
  const borrowers = loan?.borrowers || [];
  const properties = loan?.subjectProperties || [];
  const contacts = loan?.loanRelatedContacts || [];

  const handleSelectBorrower = useCallback(
    (b) =>
      dispatch(
        detailSidebarOpen(DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER, b, b?._id, {
          title:
            `${b?.firstName || ""} ${b?.lastName || ""}`.trim() || "Borrower",
          mode: "view",
          source: "LoanDrilldownSummaryTab",
          sourceField: "borrowers",
        }),
      ),
    [dispatch],
  );

  const handleSelectProperty = useCallback(
    (p) =>
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
          p,
          p?._id,
          {
            title: addressLabel(p?.address) || "Property",
            mode: "view",
            source: "LoanDrilldownSummaryTab",
            sourceField: "subjectProperties",
          },
        ),
      ),
    [dispatch],
  );

  const handleSelectContact = useCallback(
    (c) =>
      dispatch(
        detailSidebarOpen(
          DETAIL_SIDEBAR_CONTENT_TYPES.LOAN_RELATED_CONTACT,
          c,
          c?._id,
          {
            title:
              `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "Contact",
            mode: "view",
            source: "LoanDrilldownSummaryTab",
            sourceField: "loanRelatedContacts",
          },
        ),
      ),
    [dispatch],
  );

  return (
    <Box
      sx={{
        // display: "grid",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: 2,
        mb: 3,
      }}
    >
      <Card>
        <CardHead
          title="Applicants"
          icon={<PersonOutline className="thin" />}
          count={size(borrowers)}
          endContent={
            <CardLink onClick={() => navigate(`${baseUrl}/applicants`)}>
              View all →
            </CardLink>
          }
        />
        <Box>
          {borrowers.map((b) => (
            <EntityRow
              key={b._id}
              thumb={
                <ThumbBorrower>
                  <PersonOutline sx={{ fontSize: 16 }} />
                </ThumbBorrower>
              }
              name={
                `${b?.firstName || ""} ${b?.lastName || ""}`.trim() ||
                "Unnamed borrower"
              }
              meta={[
                b?.isGuarantor ? "Guarantor" : "Primary",
                b?.ficoMid ? `${b.ficoMid} FICO` : null,
                b?.verifiedExperienceTotal
                  ? `${b.verifiedExperienceTotal} prior loans`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              tag={b?.isGuarantor ? "Guarantor" : "Primary"}
              onClick={() => handleSelectBorrower(b)}
            />
          ))}
          <AddInlineRow
            label="Add borrower"
            onClick={() =>
              dispatch(
                sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_BORROWER),
              )
            }
          />
        </Box>
      </Card>

      <Card>
        <CardHead
          title="Properties"
          icon={<HomeWorkOutlined className="thin" />}
          count={size(properties)}
          endContent={
            <CardLink onClick={() => navigate(`${baseUrl}/properties`)}>
              View all →
            </CardLink>
          }
        />
        <Box>
          {properties.map((p) => {
            const cityState = [p?.address?.city, p?.address?.state]
              .filter(Boolean)
              .join(", ");
            const meta = [cityState, p?.propertyType]
              .filter(Boolean)
              .join(" · ");
            return (
              <EntityRow
                key={p._id}
                thumb={
                  <ThumbDefault>
                    <HomeOutlined sx={{ fontSize: 16 }} />
                  </ThumbDefault>
                }
                name={addressLabel(p?.address) || "Unnamed property"}
                meta={meta || null}
                tag="Subject"
                onClick={() => handleSelectProperty(p)}
              />
            );
          })}
          <AddInlineRow
            label="Add property"
            onClick={() =>
              dispatch(
                sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_PROPERTY),
              )
            }
          />
        </Box>
      </Card>

      <Card>
        <CardHead
          title="Contacts"
          icon={<ContactsOutlined className="thin" />}
          count={size(contacts)}
          endContent={
            <CardLink
              onClick={() => navigate(`${baseUrl}/loan-related-contacts`)}
            >
              View all →
            </CardLink>
          }
        />
        <Box>
          {contacts.map((c) => (
            <EntityRow
              key={c._id}
              thumb={
                <ThumbContact>
                  <PersonOutline sx={{ fontSize: 16 }} />
                </ThumbContact>
              }
              name={
                `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
                "Unnamed contact"
              }
              meta={c?.company || c?.emailAddress || null}
              tag={c?.loanRole || "Contact"}
              onClick={() => handleSelectContact(c)}
            />
          ))}
          <AddInlineRow
            label="Add contact"
            onClick={() =>
              dispatch(
                sidebarTypeToggle(
                  loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
                ),
              )
            }
          />
        </Box>
      </Card>
    </Box>
  );
};

const ThumbDefault = ({ children }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "7px",
      background: T.surface2,
      border: `1px solid ${T.line}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.accent,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const ThumbBorrower = ({ children }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "7px",
      background: T.infoSoft,
      border: `1px solid ${T.infoLine}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.info,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const ThumbContact = ({ children }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: "7px",
      background: T.warnSoft,
      border: "1px solid #e6c8a3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.warn,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const EntityRow = ({ thumb, name, meta, tag, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: "20px",
      py: "12px",
      display: "grid",
      gridTemplateColumns: "32px 1fr auto",
      gap: 1.5,
      alignItems: "center",
      borderBottom: `1px solid ${T.line2}`,
      cursor: onClick ? "pointer" : "default",
      transition: "background 0.15s",
      "&:hover": { background: T.surface3 },
    }}
  >
    {thumb}
    <Box sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 13.5,
          color: T.ink,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Txt>
      {meta && (
        <Txt
          sx={{
            fontSize: 11.5,
            color: T.ink3,
            mt: "1px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meta}
        </Txt>
      )}
    </Box>
    {tag && (
      <Txt
        sx={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: T.ink3,
          fontFamily: FONT_MONO,
          background: T.surface2,
          px: "6px",
          py: "2px",
          borderRadius: "3px",
          border: `1px solid ${T.line2}`,
          flexShrink: 0,
        }}
      >
        {tag}
      </Txt>
    )}
  </Box>
);

const AddInlineRow = ({ label, onClick }) => (
  <Flx
    ac
    g={1.25}
    onClick={onClick}
    sx={{
      px: "20px",
      py: "12px",
      color: T.ink3,
      fontSize: 12.5,
      cursor: "pointer",
      borderTop: `1px dashed ${T.line}`,
      transition: "all 0.15s",
      "&:hover": { background: T.surface3, color: T.accent },
    }}
  >
    <AddRounded sx={{ fontSize: 14 }} />
    <Box>{label}</Box>
  </Flx>
);

/* ═══════════════════════════════════════════════════════════
   ZONE 5 — COMMENTS + DOCUMENTS
   Asymmetric two-column. Comments narrower (1fr), documents
   wider (1.5fr). Composer is built into the comments card.
   Documents has search, category chips, and a 5-row preview
   with a "View all" link in the footer.
   ═══════════════════════════════════════════════════════════ */

const BottomGrid = ({ loan, baseUrl, navigate }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1.5fr" },
        gap: 3,
        mb: 3,
      }}
    >
      <CommentsCard loan={loan} />
      <DocumentsCard loan={loan} baseUrl={baseUrl} navigate={navigate} />
    </Box>
  );
};

const CommentsCard = ({ loan }) => {
  const dispatch = useDispatch();
  const comments = loan?.comments || [];
  const users = useSelector((state) => state.users);

  // Sort newest first, take 4 for the preview list. The full thread
  // lives behind the View all link → opens the existing comments sidebar.
  const previewComments = useMemo(() => {
    const sorted = [...comments].sort((a, b) => {
      const at = new Date(a?.commentDateTime || 0).getTime();
      const bt = new Date(b?.commentDateTime || 0).getTime();
      return bt - at;
    });
    return sorted.slice(0, 4);
  }, [comments]);

  const openSidebar = () =>
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.COMMENTS));

  return (
    <Card>
      <CardHead
        title="Comments"
        icon={<QuestionAnswerOutlined className="thin" />}
        count={size(comments)}
        endContent={<CardLink onClick={openSidebar}>View all →</CardLink>}
      />
      <Box sx={{ py: 1, maxHeight: 380, overflowY: "auto" }}>
        {previewComments.length === 0 ? (
          <Box sx={{ px: "20px", py: "20px" }}>
            <Txt
              sx={{
                fontSize: 12.5,
                color: T.ink3,
                fontStyle: "italic",
              }}
            >
              No comments yet — start the thread.
            </Txt>
          </Box>
        ) : (
          previewComments.map((c, idx) => (
            <CommentRow
              key={c?._id || idx}
              comment={c}
              users={users}
              isLast={idx === previewComments.length - 1}
            />
          ))
        )}
      </Box>
      <Flx
        ac
        g={1}
        sx={{
          borderTop: `1px solid ${T.line2}`,
          p: "12px 16px",
          background: T.surface3,
        }}
      >
        <TextField
          placeholder="Add a comment…"
          size="small"
          fullWidth
          // Composer kicks the user into the existing sidebar where the rich
          // editor + association picker live; treating this as the "click
          // path" rather than an inline submit keeps the flow simple.
          onClick={openSidebar}
          slotProps={{
            input: {
              readOnly: true,
              sx: {
                fontSize: 13,
                background: T.surface,
                "& fieldset": { borderColor: T.line },
              },
            },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={openSidebar}
          sx={{
            flexShrink: 0,
            background: T.accent,
            "&:hover": { background: "#234d3a" },
          }}
        >
          Post
        </Button>
      </Flx>
    </Card>
  );
};

const CommentRow = ({ comment, users, isLast }) => {
  const author = comment?.postedBy
    ? getUserFullNameFromEmail(users, comment.postedBy) || comment.postedBy
    : "Unknown";
  const initials = author
    ? author
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase()
    : "?";
  // Strip HTML out of the comment body for the preview; the full sidebar
  // renders rich content. Truncates to a single visual line.
  const text = useMemo(() => {
    const raw = comment?.htmlString || comment?.commentText || "";
    return raw.replace(/<[^>]+>/g, "").trim();
  }, [comment?.htmlString, comment?.commentText]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "28px 1fr",
        gap: 1.5,
        px: "20px",
        py: "12px",
        borderBottom: isLast ? "none" : `1px solid ${T.line2}`,
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: 10,
          fontWeight: 600,
          background: T.accent,
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Flx ac g={1} sx={{ alignItems: "baseline", mb: "3px" }}>
          <Txt sx={{ fontSize: 12.5, fontWeight: 500, color: T.ink }}>
            {author}
          </Txt>
          {comment?.commentDateTime && (
            <Txt sx={{ fontSize: 11, color: T.ink3, fontFamily: FONT_MONO }}>
              {formatDateTime(comment.commentDateTime)}
            </Txt>
          )}
        </Flx>
        <Txt
          sx={{
            fontSize: 13,
            color: T.ink2,
            lineHeight: 1.45,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {text || "—"}
        </Txt>
      </Box>
    </Box>
  );
};

/* ─── Documents ────────────────────────────────────────────
   Search + category chips + a compact 5-row preview. Filter
   chips beat sort arrows — most users want to filter to a
   group, not sort the whole list. Clicking a row dispatches
   the existing file-preview sidebar.
*/

const FILE_TONE = {
  pdf: "linear-gradient(135deg, #b54a4a 0%, #93393a 100%)",
  jpg: "linear-gradient(135deg, #a78b6f 0%, #8c7556 100%)",
  jpeg: "linear-gradient(135deg, #a78b6f 0%, #8c7556 100%)",
  png: "linear-gradient(135deg, #a78b6f 0%, #8c7556 100%)",
  xlsx: "linear-gradient(135deg, #4a8b5e 0%, #366b48 100%)",
  xls: "linear-gradient(135deg, #4a8b5e 0%, #366b48 100%)",
  docx: "linear-gradient(135deg, #4a6fb5 0%, #3a5a93 100%)",
  doc: "linear-gradient(135deg, #4a6fb5 0%, #3a5a93 100%)",
};

const DocumentsCard = ({ loan, baseUrl, navigate }) => {
  const docs = loan?.loanDocuments || [];
  const users = useSelector((state) => state.users);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  // Compute the chip categories from the actual data — top three groups by
  // count + an "All" pill. Caps at three so the toolbar doesn't grow.
  const categories = useMemo(() => {
    const counts = new Map();
    for (const d of docs) {
      if (!d?.docGroup) continue;
      counts.set(d.docGroup, (counts.get(d.docGroup) || 0) + 1);
    }
    const top = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([key]) => key);
    return ["all", ...top];
  }, [docs]);

  const filteredAndSorted = useMemo(() => {
    let result = [...docs];
    if (activeCategory !== "all") {
      result = result.filter((d) => d?.docGroup === activeCategory);
    }
    if (query) {
      const q = query.toLowerCase();
      result = result.filter((d) => {
        const name = (d?.file_display_name || d?.fileName || "").toLowerCase();
        const type = (d?.docType || "").toLowerCase();
        const group = (d?.docGroup || "").toLowerCase();
        return name.includes(q) || type.includes(q) || group.includes(q);
      });
    }
    result.sort((a, b) => {
      const at = new Date(a?.uploadDate || 0).getTime();
      const bt = new Date(b?.uploadDate || 0).getTime();
      return bt - at;
    });
    return result;
  }, [docs, query, activeCategory]);

  const previewRows = filteredAndSorted.slice(0, 5);

  return (
    <Card sx={{ mb: 0 }}>
      <CardHead
        title="Documents"
        icon={<AttachFileOutlined className="thin" />}
        count={size(docs)}
        endContent={
          <CardLink onClick={() => navigate(`${baseUrl}/documents`)}>
            View all →
          </CardLink>
        }
      />

      <Flx
        ac
        g={1.5}
        sx={{
          p: "12px 20px",
          borderBottom: `1px solid ${T.line2}`,
          background: T.surface3,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: "1 1 200px",
          }}
        >
          <SearchOutlined
            sx={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              width: 14,
              height: 14,
              color: T.ink3,
            }}
          />
          <Box
            component="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents…"
            sx={{
              width: "100%",
              background: T.surface,
              border: `1px solid ${T.line}`,
              borderRadius: "6px",
              padding: "7px 12px 7px 32px",
              fontFamily: "inherit",
              fontSize: 12.5,
              color: T.ink,
              outline: "none",
              "&:focus": { borderColor: T.accent },
            }}
          />
        </Box>
        <Flx g={0.75}>
          {categories.map((cat) => (
            <Box
              key={cat}
              component="button"
              onClick={() => setActiveCategory(cat)}
              sx={{
                background: activeCategory === cat ? T.accent : T.surface,
                color: activeCategory === cat ? "#fff" : T.ink2,
                border: `1px solid ${
                  activeCategory === cat ? T.accent : T.line
                }`,
                px: "10px",
                py: "5px",
                borderRadius: "5px",
                fontSize: 11.5,
                cursor: "pointer",
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 500,
              }}
            >
              {cat === "all" ? "All" : cat}
            </Box>
          ))}
        </Flx>
      </Flx>

      <Box>
        {previewRows.length === 0 ? (
          <Flx center column sx={{ py: 4, color: T.ink3 }}>
            <AttachFileOutlined sx={{ fontSize: 28, mb: 1 }} />
            <Txt sx={{ fontSize: 12.5 }}>
              {docs.length === 0
                ? "No documents uploaded yet."
                : "No documents match the current filter."}
            </Txt>
          </Flx>
        ) : (
          <Box
            sx={{
              "& > *:last-child .doc-cell": { borderBottom: "none" },
            }}
          >
            {previewRows.map((d) => (
              <DocRow key={d._id} doc={d} users={users} />
            ))}
          </Box>
        )}
      </Box>

      <Flx
        ac
        jb
        sx={{
          p: "12px 20px",
          borderTop: `1px solid ${T.line2}`,
          background: T.surface3,
        }}
      >
        <Txt sx={{ fontSize: 12, color: T.ink3, fontFamily: FONT_MONO }}>
          Showing {previewRows.length} of {filteredAndSorted.length}
        </Txt>
        <CardLink onClick={() => navigate(`${baseUrl}/documents`)}>
          Open document library →
        </CardLink>
      </Flx>
    </Card>
  );
};

const DocRow = ({ doc, users }) => {
  const ext = useMemo(() => {
    const name = doc?.file_display_name || doc?.fileName || "";
    const idx = name.lastIndexOf(".");
    return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
  }, [doc]);
  const tone = FILE_TONE[ext] || FILE_TONE.pdf;
  const uploadedBy = doc?.uploadedBy
    ? getUserFullNameFromEmail(users, doc.uploadedBy) || doc.uploadedBy
    : "—";

  return (
    <Box
      className="doc-cell"
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.5fr) 120px 110px 130px",
        gap: 1.5,
        alignItems: "center",
        px: "20px",
        py: "10px",
        borderBottom: `1px solid ${T.line2}`,
        cursor: "pointer",
        transition: "background 0.12s",
        "&:hover": { background: T.surface3 },
      }}
    >
      <Flx ac g={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 600,
            fontFamily: FONT_MONO,
            color: "#fff",
            background: tone,
            flexShrink: 0,
          }}
        >
          {ext.toUpperCase() || "FILE"}
        </Box>
        <Txt
          sx={{
            color: T.ink,
            fontWeight: 500,
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doc?.file_display_name || doc?.fileName || "Untitled"}
        </Txt>
      </Flx>
      <Txt
        sx={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: "4px",
          background: T.surface2,
          color: T.ink2,
          fontSize: 11,
          fontWeight: 500,
          border: `1px solid ${T.line2}`,
          width: "fit-content",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {doc?.docGroup || "Uncategorized"}
      </Txt>
      <Txt
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 11.5,
          color: T.ink3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {doc?.uploadDate ? formatDate(doc.uploadDate) : "—"}
      </Txt>
      <Txt
        sx={{
          fontSize: 12,
          color: T.ink2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {uploadedBy}
      </Txt>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════
   SHARED CARD CHROME
   ═══════════════════════════════════════════════════════════ */

export const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: T.surface,
      border: `1px solid ${T.line}`,
      borderRadius: "12px",
      boxShadow: SHADOW_SM,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const CardHead = ({ title, count, endContent, icon }) => (
  <Flx
    ac
    jb
    sx={{
      p: "16px 20px",
      borderBottom: `1px solid ${T.line2}`,
    }}
  >
    <Flx ac g={1}>
      {icon && (
        <Flx
          center
          sx={{
            color: T.ink,
            ".MuiSvgIcon-root": { fontSize: "18px !important" },
          }}
        >
          {icon}
        </Flx>
      )}
      <Txt
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: "-0.01em",
          m: 0,
          color: T.ink,
        }}
      >
        {title}
      </Txt>
      {count != null && (
        <Txt
          sx={{
            fontSize: 11,
            fontFamily: FONT_MONO,
            color: T.ink3,
            background: T.surface2,
            px: "7px",
            py: "2px",
            borderRadius: "4px",
            border: `1px solid ${T.line2}`,
            fontWeight: 400,
          }}
        >
          {count}
        </Txt>
      )}
    </Flx>
    {endContent}
  </Flx>
);

export const CardLink = ({ children, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      fontSize: 11.5,
      color: T.accent,
      cursor: "pointer",
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      fontFamily: FONT_MONO,
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {children}
  </Box>
);
